import { createOtfAllocators, createGatewayConnection, createHydraPlatform, HydraPlatform, PlatformConfig, VersionNegotiation } from "@adaptive/hydra-platform";
import { map, mergeMap, Observable, of, ReplaySubject, take } from "rxjs";
import { DecimalFloatConverter } from "@adaptive/hydra-codecs/dist/valueConverters";
import { LocalDateConverter } from "@adaptive/hydra-codecs/dist/valueConverters";

export const SUCCESS_WATCH_LIST_ACK_RESPONSE = 'success', ERROR_WATCH_LIST_ACK_RESPONSE = 'error';

export type SuccessWatchListAckResponse = { type: typeof SUCCESS_WATCH_LIST_ACK_RESPONSE };
export type ErrorWatchListAckResponse = { type: typeof ERROR_WATCH_LIST_ACK_RESPONSE, payload: EquityError };
export type WatchListAckResponse = SuccessWatchListAckResponse | ErrorWatchListAckResponse;

export interface EquityError {
    errorType: EquityErrorType;
    message: string;
}

export enum EquityErrorType {
    ClientError = "ClientError",
    AlpacaError = "AlpacaError",
    InternalError = "InternalError",
    ServiceUnavailable = "ServiceUnavailable"
}

export interface UpdateWatchListRequest {
    watchlistId: WatchListId;
    requestBody: WatchListRequestBody;
}

export interface WatchListRequestBody {
    name: string;
    symbols: Array<string>;
}

export interface AddOrRemoveAssetRequest {
    watchlistId: WatchListId;
    symbol: string;
}

export interface CreateWatchListRequest {
    requestBody: WatchListRequestBody;
}

export const SUCCESS_WATCH_LIST_RESPONSE = 'success', ERROR_WATCH_LIST_RESPONSE = 'error';

export type SuccessWatchListResponse = { type: typeof SUCCESS_WATCH_LIST_RESPONSE, payload: WatchList };
export type ErrorWatchListResponse = { type: typeof ERROR_WATCH_LIST_RESPONSE, payload: EquityError };
export type WatchListResponse = SuccessWatchListResponse | ErrorWatchListResponse;

export interface WatchList {
    id: string;
    accountId: string;
    createdAt: string;
    updatedAt: string;
    name: string;
    assets: Array<Instrument>;
}

export interface Instrument {
    symbol: string;
    name: string;
    displayName: string;
    exchange: string;
    sector: string;
    industry: string;
    marketCapitalization: string;
    tradable: boolean;
    status: string;
}

export interface WatchListId {
    id: string;
}

export const SUCCESS_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE = 'success', ERROR_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE = 'error';

export type SuccessGetWatchListsByAccountIdResponse = { type: typeof SUCCESS_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE, payload: Array<WatchListDetails> };
export type ErrorGetWatchListsByAccountIdResponse = { type: typeof ERROR_GET_WATCH_LISTS_BY_ACCOUNT_ID_RESPONSE, payload: EquityError };
export type GetWatchListsByAccountIdResponse = SuccessGetWatchListsByAccountIdResponse | ErrorGetWatchListsByAccountIdResponse;

export interface WatchListDetails {
    id: string;
    accountId: string;
    createdAt: string;
    updatedAt: string;
    name: string;
}

export const SUCCESS_EQUITY_ORDER_ACK_RESPONSE = 'success', ERROR_EQUITY_ORDER_ACK_RESPONSE = 'error';

export type SuccessEquityOrderAckResponse = { type: typeof SUCCESS_EQUITY_ORDER_ACK_RESPONSE };
export type ErrorEquityOrderAckResponse = { type: typeof ERROR_EQUITY_ORDER_ACK_RESPONSE, payload: EquityError };
export type EquityOrderAckResponse = SuccessEquityOrderAckResponse | ErrorEquityOrderAckResponse;

export interface OrderId {
    id: string;
}

export const SUCCESS_EQUITY_ORDER_UPDATE_RESULT = 'success', ERROR_EQUITY_ORDER_UPDATE_RESULT = 'error';

export type SuccessEquityOrderUpdateResult = { type: typeof SUCCESS_EQUITY_ORDER_UPDATE_RESULT, payload: EquityOrderUpdate };
export type ErrorEquityOrderUpdateResult = { type: typeof ERROR_EQUITY_ORDER_UPDATE_RESULT, payload: EquityError };
export type EquityOrderUpdateResult = SuccessEquityOrderUpdateResult | ErrorEquityOrderUpdateResult;

export interface EquityOrderUpdate {
    updateType: UpdateType;
    order: EquityOrder;
}

export interface EquityOrder {
    id: string;
    symbol: string;
    side: Direction;
    notional: number;
    qty: number;
    orderType: OrderType;
    timeInForce: TimeInForce;
    status: OrderStatus;
    createdAt: bigint;
    updateAt: bigint;
    filledQty: number;
}

export enum OrderStatus {
    Accepted = "Accepted",
    PendingNew = "PendingNew",
    Pending = "Pending",
    New = "New",
    PartiallyFilled = "PartiallyFilled",
    Filled = "Filled",
    PendingCancel = "PendingCancel",
    Canceled = "Canceled",
    Expired = "Expired",
    PendingReplace = "PendingReplace",
    Replaced = "Replaced",
    DoneForDay = "DoneForDay",
    Rejected = "Rejected",
    AcceptedForBidding = "AcceptedForBidding",
    Stopped = "Stopped",
    Suspended = "Suspended",
    Calculated = "Calculated"
}

export enum TimeInForce {
    Day = "Day",
    Gtc = "Gtc",
    Opg = "Opg",
    Cls = "Cls",
    Ioc = "Ioc",
    Fok = "Fok"
}

export enum OrderType {
    Market = "Market",
    Limit = "Limit",
    Stop = "Stop",
    StopLimit = "StopLimit"
}

export enum Direction {
    Buy = "Buy",
    Sell = "Sell"
}

export enum UpdateType {
    StateOfTheWorld = "StateOfTheWorld",
    Updated = "Updated",
    Added = "Added",
    Deleted = "Deleted"
}

export interface UpdateEquityOrderRequest {
    orderId: string;
    qty: number;
    timeInForce: TimeInForce;
    limitPrice: number;
    stopPrice: number;
}

export const SUCCESS_EQUITY_ORDER_RESPONSE = 'success', ERROR_EQUITY_ORDER_RESPONSE = 'error';

export type SuccessEquityOrderResponse = { type: typeof SUCCESS_EQUITY_ORDER_RESPONSE, payload: EquityOrder };
export type ErrorEquityOrderResponse = { type: typeof ERROR_EQUITY_ORDER_RESPONSE, payload: EquityError };
export type EquityOrderResponse = SuccessEquityOrderResponse | ErrorEquityOrderResponse;

export interface PlaceEquityOrderRequest {
    symbol: string;
    side: Direction;
    notional: number;
    qty: number;
    orderType: OrderType;
    timeInForce: TimeInForce;
    limitPrice: number;
    stopPrice: number;
}

export const SUCCESS_MARKET_STATUS_RESULT = 'success', ERROR_MARKET_STATUS_RESULT = 'error';

export type SuccessMarketStatusResult = { type: typeof SUCCESS_MARKET_STATUS_RESULT, payload: MarketOpenResponse };
export type ErrorMarketStatusResult = { type: typeof ERROR_MARKET_STATUS_RESULT, payload: EquityError };
export type MarketStatusResult = SuccessMarketStatusResult | ErrorMarketStatusResult;

export interface MarketOpenResponse {
    isMarketOpen: boolean;
}

export const SUCCESS_LATEST_BAR_RESULT = 'success', ERROR_LATEST_BAR_RESULT = 'error';

export type SuccessLatestBarResult = { type: typeof SUCCESS_LATEST_BAR_RESULT, payload: BarResponse };
export type ErrorLatestBarResult = { type: typeof ERROR_LATEST_BAR_RESULT, payload: EquityError };
export type LatestBarResult = SuccessLatestBarResult | ErrorLatestBarResult;

export interface BarResponse {
    symbol: string;
    o: DecimalFloat;
    h: DecimalFloat;
    l: DecimalFloat;
    c: DecimalFloat;
    v: number;
    n: number;
    vw: DecimalFloat;
    t: string;
}

export type DecimalFloat = DecimalFloatConverter.ConvertedType;

export interface LatestBarRequest {
    symbols: string;
}

export const SUCCESS_QUOTE_RESULT = 'success', ERROR_QUOTE_RESULT = 'error';

export type SuccessQuoteResult = { type: typeof SUCCESS_QUOTE_RESULT, payload: EquityQuoteResponse };
export type ErrorQuoteResult = { type: typeof ERROR_QUOTE_RESULT, payload: EquityError };
export type QuoteResult = SuccessQuoteResult | ErrorQuoteResult;

export interface EquityQuoteResponse {
    symbol: string;
    ap: DecimalFloat;
    bp: DecimalFloat;
    t: string;
}

export interface EquityQuoteRequest {
    symbols: string;
}

export const SUCCESS_HISTORICAL_BAR_RESULT = 'success', ERROR_HISTORICAL_BAR_RESULT = 'error';

export type SuccessHistoricalBarResult = { type: typeof SUCCESS_HISTORICAL_BAR_RESULT, payload: BarResponse };
export type ErrorHistoricalBarResult = { type: typeof ERROR_HISTORICAL_BAR_RESULT, payload: EquityError };
export type HistoricalBarResult = SuccessHistoricalBarResult | ErrorHistoricalBarResult;

export interface HistoricalBarRequest {
    symbols: string;
    startTime: string;
    endTime: string;
    limit: number;
    timeframe: string;
}

export const SUCCESS_ACCOUNT_SUMMARY_RESULT = 'success', ERROR_ACCOUNT_SUMMARY_RESULT = 'error';

export type SuccessAccountSummaryResult = { type: typeof SUCCESS_ACCOUNT_SUMMARY_RESULT, payload: AccountSummaryUpdate };
export type ErrorAccountSummaryResult = { type: typeof ERROR_ACCOUNT_SUMMARY_RESULT, payload: EquityError };
export type AccountSummaryResult = SuccessAccountSummaryResult | ErrorAccountSummaryResult;

export interface AccountSummaryUpdate {
    updateType: UpdateType;
    payload: AccountSummary;
}

export interface AccountSummary {
    accountNumber: string;
    cash: DecimalFloat;
    buyingPower: DecimalFloat;
    portfolioValue: DecimalFloat;
}

export const SUCCESS_EQUITY_INSTRUMENT_RESULT = 'success', ERROR_EQUITY_INSTRUMENT_RESULT = 'error';

export type SuccessEquityInstrumentResult = { type: typeof SUCCESS_EQUITY_INSTRUMENT_RESULT, payload: EquityInstrumentUpdate };
export type ErrorEquityInstrumentResult = { type: typeof ERROR_EQUITY_INSTRUMENT_RESULT, payload: EquityError };
export type EquityInstrumentResult = SuccessEquityInstrumentResult | ErrorEquityInstrumentResult;

export interface EquityInstrumentUpdate {
    payload: Instrument;
}

export interface InstrumentRequest {
    searchStr: string;
    symbols: Array<string>;
    limit: number;
}

export const SUCCESS_POSITION_RESULT = 'success', ERROR_POSITION_RESULT = 'error';

export type SuccessPositionResult = { type: typeof SUCCESS_POSITION_RESULT, payload: PositionUpdate };
export type ErrorPositionResult = { type: typeof ERROR_POSITION_RESULT, payload: EquityError };
export type PositionResult = SuccessPositionResult | ErrorPositionResult;

export interface PositionUpdate {
    updateType: UpdateType;
    payload: EquityPosition;
}

export interface EquityPosition {
    assetId: string;
    symbol: string;
    exchange: string;
    assetClass: string;
    avgEntryPrice: string;
    qty: string;
    side: PositionSide;
    marketValue: DecimalFloat;
    costBasis: DecimalFloat;
    unrealizedPl: DecimalFloat;
    unrealizedPlpc: DecimalFloat;
    unrealizedIntradayPl: DecimalFloat;
    unrealizedIntradayPlpc: DecimalFloat;
    currentPrice: DecimalFloat;
    lastDayPrice: DecimalFloat;
    changeToday: DecimalFloat;
}

export enum PositionSide {
    Long = "Long",
    Short = "Short"
}

export interface ExecutionResponse {
    trade: Trade;
}

export interface Trade {
    tradeId: bigint;
    tradeName: string;
    currencyPair: string;
    notional: number;
    dealtCurrency: string;
    direction: Direction;
    spotRate: number;
    status: TradeStatus;
    tradeDate: LocalDate;
    valueDate: LocalDate;
}

export type LocalDate = LocalDateConverter.ConvertedType;

export enum TradeStatus {
    Pending = "Pending",
    Done = "Done",
    Rejected = "Rejected"
}

export interface ExecuteTradeRequest {
    currencyPair: string;
    spotRate: number;
    valueDate: LocalDate;
    direction: Direction;
    notional: number;
    dealtCurrency: string;
}

export const RFQ_CREATED_RFQ_UPDATE = 'rfqCreated', QUOTE_CREATED_RFQ_UPDATE = 'quoteCreated', QUOTE_QUOTED_RFQ_UPDATE = 'quoteQuoted', QUOTE_PASSED_RFQ_UPDATE = 'quotePassed', QUOTE_ACCEPTED_RFQ_UPDATE = 'quoteAccepted', RFQ_CLOSED_RFQ_UPDATE = 'rfqClosed', START_OF_STATE_OF_THE_WORLD_RFQ_UPDATE = 'startOfStateOfTheWorld', END_OF_STATE_OF_THE_WORLD_RFQ_UPDATE = 'endOfStateOfTheWorld';

export type RfqCreatedRfqUpdate = { type: typeof RFQ_CREATED_RFQ_UPDATE, payload: RfqBody };
export type QuoteCreatedRfqUpdate = { type: typeof QUOTE_CREATED_RFQ_UPDATE, payload: QuoteBody };
export type QuoteQuotedRfqUpdate = { type: typeof QUOTE_QUOTED_RFQ_UPDATE, payload: QuoteBody };
export type QuotePassedRfqUpdate = { type: typeof QUOTE_PASSED_RFQ_UPDATE, payload: QuoteBody };
export type QuoteAcceptedRfqUpdate = { type: typeof QUOTE_ACCEPTED_RFQ_UPDATE, payload: QuoteBody };
export type RfqClosedRfqUpdate = { type: typeof RFQ_CLOSED_RFQ_UPDATE, payload: RfqBody };
export type StartOfStateOfTheWorldRfqUpdate = { type: typeof START_OF_STATE_OF_THE_WORLD_RFQ_UPDATE };
export type EndOfStateOfTheWorldRfqUpdate = { type: typeof END_OF_STATE_OF_THE_WORLD_RFQ_UPDATE };
export type RfqUpdate = RfqCreatedRfqUpdate | QuoteCreatedRfqUpdate | QuoteQuotedRfqUpdate | QuotePassedRfqUpdate | QuoteAcceptedRfqUpdate | RfqClosedRfqUpdate | StartOfStateOfTheWorldRfqUpdate | EndOfStateOfTheWorldRfqUpdate;

export interface QuoteBody {
    id: number;
    rfqId: number;
    dealerId: number;
    state: QuoteState;
}

export const PENDING_WITHOUT_PRICE_QUOTE_STATE = 'pendingWithoutPrice', PENDING_WITH_PRICE_QUOTE_STATE = 'pendingWithPrice', PASSED_QUOTE_STATE = 'passed', ACCEPTED_QUOTE_STATE = 'accepted', REJECTED_WITH_PRICE_QUOTE_STATE = 'rejectedWithPrice', REJECTED_WITHOUT_PRICE_QUOTE_STATE = 'rejectedWithoutPrice';

export type PendingWithoutPriceQuoteState = { type: typeof PENDING_WITHOUT_PRICE_QUOTE_STATE };
export type PendingWithPriceQuoteState = { type: typeof PENDING_WITH_PRICE_QUOTE_STATE, payload: number };
export type PassedQuoteState = { type: typeof PASSED_QUOTE_STATE };
export type AcceptedQuoteState = { type: typeof ACCEPTED_QUOTE_STATE, payload: number };
export type RejectedWithPriceQuoteState = { type: typeof REJECTED_WITH_PRICE_QUOTE_STATE, payload: number };
export type RejectedWithoutPriceQuoteState = { type: typeof REJECTED_WITHOUT_PRICE_QUOTE_STATE };
export type QuoteState = PendingWithoutPriceQuoteState | PendingWithPriceQuoteState | PassedQuoteState | AcceptedQuoteState | RejectedWithPriceQuoteState | RejectedWithoutPriceQuoteState;

export interface RfqBody {
    id: number;
    instrumentId: number;
    quantity: number;
    direction: Direction;
    state: RfqState;
    expirySecs: number;
    creationTimestamp: bigint;
}

export enum RfqState {
    Open = "Open",
    Expired = "Expired",
    Cancelled = "Cancelled",
    Closed = "Closed"
}

export const ACK_ACCEPT_QUOTE_RESPONSE = 'ack', NACK_ACCEPT_QUOTE_RESPONSE = 'nack';

export type AckAcceptQuoteResponse = { type: typeof ACK_ACCEPT_QUOTE_RESPONSE };
export type NackAcceptQuoteResponse = { type: typeof NACK_ACCEPT_QUOTE_RESPONSE };
export type AcceptQuoteResponse = AckAcceptQuoteResponse | NackAcceptQuoteResponse;

export interface AcceptQuoteRequest {
    quoteId: number;
}

export const ACK_PASS_RESPONSE = 'ack', NACK_PASS_RESPONSE = 'nack';

export type AckPassResponse = { type: typeof ACK_PASS_RESPONSE };
export type NackPassResponse = { type: typeof NACK_PASS_RESPONSE };
export type PassResponse = AckPassResponse | NackPassResponse;

export interface PassRequest {
    quoteId: number;
}

export const ACK_QUOTE_RESPONSE = 'ack', NACK_QUOTE_RESPONSE = 'nack';

export type AckQuoteResponse = { type: typeof ACK_QUOTE_RESPONSE };
export type NackQuoteResponse = { type: typeof NACK_QUOTE_RESPONSE };
export type QuoteResponse = AckQuoteResponse | NackQuoteResponse;

export interface QuoteRequest {
    quoteId: number;
    price: number;
}

export interface CancelRfqRequest {
    rfqId: number;
}

export const ACK_CREATE_RFQ_RESPONSE = 'ack', NACK_CREATE_RFQ_RESPONSE = 'nack';

export type AckCreateRfqResponse = { type: typeof ACK_CREATE_RFQ_RESPONSE, payload: number };
export type NackCreateRfqResponse = { type: typeof NACK_CREATE_RFQ_RESPONSE };
export type CreateRfqResponse = AckCreateRfqResponse | NackCreateRfqResponse;

export interface CreateRfqRequest {
    instrumentId: number;
    dealerIds: Array<number>;
    quantity: number;
    direction: Direction;
    expirySecs: number;
}

export const ADDED_DEALER_UPDATE = 'added', REMOVED_DEALER_UPDATE = 'removed', START_OF_STATE_OF_THE_WORLD_DEALER_UPDATE = 'startOfStateOfTheWorld', END_OF_STATE_OF_THE_WORLD_DEALER_UPDATE = 'endOfStateOfTheWorld';

export type AddedDealerUpdate = { type: typeof ADDED_DEALER_UPDATE, payload: DealerBody };
export type RemovedDealerUpdate = { type: typeof REMOVED_DEALER_UPDATE, payload: number };
export type StartOfStateOfTheWorldDealerUpdate = { type: typeof START_OF_STATE_OF_THE_WORLD_DEALER_UPDATE };
export type EndOfStateOfTheWorldDealerUpdate = { type: typeof END_OF_STATE_OF_THE_WORLD_DEALER_UPDATE };
export type DealerUpdate = AddedDealerUpdate | RemovedDealerUpdate | StartOfStateOfTheWorldDealerUpdate | EndOfStateOfTheWorldDealerUpdate;

export interface DealerBody {
    id: number;
    name: string;
}

export const ADDED_INSTRUMENT_UPDATE = 'added', REMOVED_INSTRUMENT_UPDATE = 'removed', START_OF_STATE_OF_THE_WORLD_INSTRUMENT_UPDATE = 'startOfStateOfTheWorld', END_OF_STATE_OF_THE_WORLD_INSTRUMENT_UPDATE = 'endOfStateOfTheWorld';

export type AddedInstrumentUpdate = { type: typeof ADDED_INSTRUMENT_UPDATE, payload: InstrumentBody };
export type RemovedInstrumentUpdate = { type: typeof REMOVED_INSTRUMENT_UPDATE, payload: number };
export type StartOfStateOfTheWorldInstrumentUpdate = { type: typeof START_OF_STATE_OF_THE_WORLD_INSTRUMENT_UPDATE };
export type EndOfStateOfTheWorldInstrumentUpdate = { type: typeof END_OF_STATE_OF_THE_WORLD_INSTRUMENT_UPDATE };
export type InstrumentUpdate = AddedInstrumentUpdate | RemovedInstrumentUpdate | StartOfStateOfTheWorldInstrumentUpdate | EndOfStateOfTheWorldInstrumentUpdate;

export interface InstrumentBody {
    id: number;
    name: string;
    cusip: string;
    ticker: string;
    maturity: LocalDate;
    interestRate: number;
    benchmark: string;
}

export interface EchoResponse {
    payload: number;
}

export interface EchoRequest {
    payload: number;
}

export interface SetThroughputRequest {
    targetUpdatesPerSecond: number;
}

export interface GetThroughputResponse {
    updatesPerSecond: number;
}

export interface CurrencyPairUpdates {
    updates: Array<CurrencyPairUpdate>;
    isStateOfTheWorld: boolean;
    isStale: boolean;
}

export const ADDED_CURRENCY_PAIR_UPDATE = 'added', REMOVED_CURRENCY_PAIR_UPDATE = 'removed';

export type AddedCurrencyPairUpdate = { type: typeof ADDED_CURRENCY_PAIR_UPDATE, payload: CurrencyPair };
export type RemovedCurrencyPairUpdate = { type: typeof REMOVED_CURRENCY_PAIR_UPDATE, payload: CurrencyPair };
export type CurrencyPairUpdate = AddedCurrencyPairUpdate | RemovedCurrencyPairUpdate;

export interface CurrencyPair {
    symbol: string;
    ratePrecision: number;
    pipsPosition: number;
}

export interface PriceTickHistory {
    prices: Array<PriceTick>;
}

export interface PriceTick {
    symbol: string;
    bid: number;
    ask: number;
    mid: number;
    valueDate: LocalDate;
    creationTimestamp: bigint;
}

export interface PriceStreamRequest {
    symbol: string;
}

export interface TradeUpdates {
    updates: Array<Trade>;
    isStateOfTheWorld: boolean;
    isStale: boolean;
}

export const SUCCESS_LOGOUT_RESPONSE = 'success';

export type SuccessLogoutResponse = { type: typeof SUCCESS_LOGOUT_RESPONSE };
export type LogoutResponse = SuccessLogoutResponse;

export const SUCCESS_LOGIN_RESPONSE = 'success', ERROR_LOGIN_RESPONSE = 'error';

export type SuccessLoginResponse = { type: typeof SUCCESS_LOGIN_RESPONSE, payload: LoginSuccess };
export type ErrorLoginResponse = { type: typeof ERROR_LOGIN_RESPONSE, payload: LoginError };
export type LoginResponse = SuccessLoginResponse | ErrorLoginResponse;

export enum LoginError {
    InvalidCredentials = "InvalidCredentials",
    InternalError = "InternalError",
    ServiceUnavailable = "ServiceUnavailable"
}

export interface LoginSuccess {
    username: string;
    accountId: string;
}

export interface LoginRequest {
    username: string;
}

export interface PositionUpdates {
    currentPositions: Array<CurrencyPairPosition>;
    history: Array<HistoricPosition>;
}

export interface HistoricPosition {
    timestamp: string;
    usdPnl: number;
}

export interface CurrencyPairPosition {
    symbol: string;
    basePnl: number;
    baseTradedAmount: number;
    counterTradedAmount: number;
}

export interface AnalyticsRequest {
    currency: string;
}

const converters = {DecimalFloatConverter,LocalDateConverter};
const allocators = createOtfAllocators(converters);

function WatchListAckResponseTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:undefined},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function EquityErrorRefTypeDefinition() {
    return {type:"pointer"as const,elementType:EquityErrorTypeDefinition};
}

function EquityErrorTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{errorType:{location:{bitOffset:0,byteOffset:0,mask:0},type:EquityErrorTypeTypeDefinition},message:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function stringRefTypeDefinition() {
    return {type:"pointer"as const,elementType:stringTypeDefinition};
}

function stringTypeDefinition() {
    return {type:"string"as const,count:{encodingType:"Int32"as const,location:{bitOffset:0,byteOffset:0,mask:0}},encoding:"Utf16"as const,maxCount:undefined};
}

function EquityErrorTypeTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"ClientError"as const,value:BigInt("1")},{name:"AlpacaError"as const,value:BigInt("2")},{name:"InternalError"as const,value:BigInt("3")},{name:"ServiceUnavailable"as const,value:BigInt("4")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function UpdateWatchListRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{watchlistId:{location:{bitOffset:0,byteOffset:0,mask:0},type:WatchListIdRefTypeDefinition},requestBody:{location:{bitOffset:32,byteOffset:4,mask:0},type:WatchListRequestBodyRefTypeDefinition}},jsonConverter:undefined};
}

function WatchListRequestBodyRefTypeDefinition() {
    return {type:"pointer"as const,elementType:WatchListRequestBodyTypeDefinition};
}

function WatchListRequestBodyTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{name:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},symbols:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefListRefTypeDefinition}},jsonConverter:undefined};
}

function stringRefListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:stringRefListTypeDefinition};
}

function stringRefListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:32,byteLength:4},elementType:stringRefTypeDefinition,lengthEncoding:undefined,firstElementOffset:2,maxCount:undefined};
}

function WatchListIdRefTypeDefinition() {
    return {type:"pointer"as const,elementType:WatchListIdTypeDefinition};
}

function AddOrRemoveAssetRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{watchlistId:{location:{bitOffset:0,byteOffset:0,mask:0},type:WatchListIdRefTypeDefinition},symbol:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function CreateWatchListRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{requestBody:{location:{bitOffset:0,byteOffset:0,mask:0},type:WatchListRequestBodyRefTypeDefinition}},jsonConverter:undefined};
}

function WatchListResponseTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:WatchListRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function WatchListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:WatchListTypeDefinition};
}

function WatchListTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:192,byteLength:24},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},accountId:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition},createdAt:{location:{bitOffset:64,byteOffset:8,mask:0},type:stringRefTypeDefinition},updatedAt:{location:{bitOffset:96,byteOffset:12,mask:0},type:stringRefTypeDefinition},name:{location:{bitOffset:128,byteOffset:16,mask:0},type:stringRefTypeDefinition},assets:{location:{bitOffset:160,byteOffset:20,mask:0},type:InstrumentListRefTypeDefinition}},jsonConverter:undefined};
}

function InstrumentListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:InstrumentListTypeDefinition};
}

function InstrumentListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:288,byteLength:36},elementType:InstrumentTypeDefinition,lengthEncoding:{encodingType:"UInt24"as const,location:{bitOffset:16,byteOffset:2,mask:0}},firstElementOffset:5,maxCount:undefined};
}

function InstrumentTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:288,byteLength:36},fields:{symbol:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},name:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition},displayName:{location:{bitOffset:64,byteOffset:8,mask:0},type:stringRefTypeDefinition},exchange:{location:{bitOffset:96,byteOffset:12,mask:0},type:stringRefTypeDefinition},sector:{location:{bitOffset:128,byteOffset:16,mask:0},type:stringRefTypeDefinition},industry:{location:{bitOffset:160,byteOffset:20,mask:0},type:stringRefTypeDefinition},marketCapitalization:{location:{bitOffset:192,byteOffset:24,mask:0},type:stringRefTypeDefinition},tradable:{location:{bitOffset:224,byteOffset:28,mask:1},type:boolTypeDefinition},status:{location:{bitOffset:256,byteOffset:32,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function boolTypeDefinition() {
    return "Bool"as const;
}

function WatchListIdTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function GetWatchListsByAccountIdResponseTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:WatchListDetailsListRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function WatchListDetailsListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:WatchListDetailsListTypeDefinition};
}

function WatchListDetailsListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:160,byteLength:20},elementType:WatchListDetailsTypeDefinition,lengthEncoding:{encodingType:"UInt24"as const,location:{bitOffset:16,byteOffset:2,mask:0}},firstElementOffset:5,maxCount:undefined};
}

function WatchListDetailsTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:160,byteLength:20},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},accountId:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition},createdAt:{location:{bitOffset:64,byteOffset:8,mask:0},type:stringRefTypeDefinition},updatedAt:{location:{bitOffset:96,byteOffset:12,mask:0},type:stringRefTypeDefinition},name:{location:{bitOffset:128,byteOffset:16,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function EquityOrderAckResponseTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:undefined},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function OrderIdTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function EquityOrderUpdateResultTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityOrderUpdateRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function EquityOrderUpdateRefTypeDefinition() {
    return {type:"pointer"as const,elementType:EquityOrderUpdateTypeDefinition};
}

function EquityOrderUpdateTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{updateType:{location:{bitOffset:0,byteOffset:0,mask:0},type:UpdateTypeTypeDefinition},order:{location:{bitOffset:32,byteOffset:4,mask:0},type:EquityOrderRefTypeDefinition}},jsonConverter:undefined};
}

function EquityOrderRefTypeDefinition() {
    return {type:"pointer"as const,elementType:EquityOrderTypeDefinition};
}

function EquityOrderTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:416,byteLength:52},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},symbol:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition},side:{location:{bitOffset:64,byteOffset:8,mask:0},type:DirectionTypeDefinition},notional:{location:{bitOffset:72,byteOffset:9,mask:0},type:NotionalTypeDefinition},qty:{location:{bitOffset:136,byteOffset:17,mask:0},type:QuantityTypeDefinition},orderType:{location:{bitOffset:200,byteOffset:25,mask:0},type:OrderTypeTypeDefinition},timeInForce:{location:{bitOffset:208,byteOffset:26,mask:0},type:TimeInForceTypeDefinition},status:{location:{bitOffset:216,byteOffset:27,mask:0},type:OrderStatusTypeDefinition},createdAt:{location:{bitOffset:224,byteOffset:28,mask:0},type:int64TypeDefinition},updateAt:{location:{bitOffset:288,byteOffset:36,mask:0},type:int64TypeDefinition},filledQty:{location:{bitOffset:352,byteOffset:44,mask:0},type:QuantityTypeDefinition}},jsonConverter:undefined};
}

function int64TypeDefinition() {
    return "Int64"as const;
}

function OrderStatusTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"Accepted"as const,value:BigInt("1")},{name:"PendingNew"as const,value:BigInt("2")},{name:"Pending"as const,value:BigInt("3")},{name:"New"as const,value:BigInt("4")},{name:"PartiallyFilled"as const,value:BigInt("5")},{name:"Filled"as const,value:BigInt("6")},{name:"PendingCancel"as const,value:BigInt("7")},{name:"Canceled"as const,value:BigInt("8")},{name:"Expired"as const,value:BigInt("9")},{name:"PendingReplace"as const,value:BigInt("10")},{name:"Replaced"as const,value:BigInt("11")},{name:"DoneForDay"as const,value:BigInt("12")},{name:"Rejected"as const,value:BigInt("13")},{name:"AcceptedForBidding"as const,value:BigInt("14")},{name:"Stopped"as const,value:BigInt("15")},{name:"Suspended"as const,value:BigInt("16")},{name:"Calculated"as const,value:BigInt("17")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function TimeInForceTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"Day"as const,value:BigInt("1")},{name:"Gtc"as const,value:BigInt("2")},{name:"Opg"as const,value:BigInt("3")},{name:"Cls"as const,value:BigInt("4")},{name:"Ioc"as const,value:BigInt("5")},{name:"Fok"as const,value:BigInt("6")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function OrderTypeTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"Market"as const,value:BigInt("1")},{name:"Limit"as const,value:BigInt("2")},{name:"Stop"as const,value:BigInt("3")},{name:"StopLimit"as const,value:BigInt("4")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function QuantityTypeDefinition() {
    return "Float64"as const;
}

function NotionalTypeDefinition() {
    return "Float64"as const;
}

function DirectionTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"Buy"as const,value:BigInt("1")},{name:"Sell"as const,value:BigInt("2")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function UpdateTypeTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"StateOfTheWorld"as const,value:BigInt("1")},{name:"Updated"as const,value:BigInt("2")},{name:"Added"as const,value:BigInt("3")},{name:"Deleted"as const,value:BigInt("4")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function UpdateEquityOrderRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:232,byteLength:29},fields:{orderId:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},qty:{location:{bitOffset:32,byteOffset:4,mask:0},type:QuantityTypeDefinition},timeInForce:{location:{bitOffset:96,byteOffset:12,mask:0},type:TimeInForceTypeDefinition},limitPrice:{location:{bitOffset:104,byteOffset:13,mask:0},type:LimitPriceTypeDefinition},stopPrice:{location:{bitOffset:168,byteOffset:21,mask:0},type:StopPriceTypeDefinition}},jsonConverter:undefined};
}

function StopPriceTypeDefinition() {
    return "Float64"as const;
}

function LimitPriceTypeDefinition() {
    return "Float64"as const;
}

function EquityOrderResponseTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityOrderRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function PlaceEquityOrderRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:312,byteLength:39},fields:{symbol:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},side:{location:{bitOffset:32,byteOffset:4,mask:0},type:DirectionTypeDefinition},notional:{location:{bitOffset:40,byteOffset:5,mask:0},type:NotionalTypeDefinition},qty:{location:{bitOffset:104,byteOffset:13,mask:0},type:QuantityTypeDefinition},orderType:{location:{bitOffset:168,byteOffset:21,mask:0},type:OrderTypeTypeDefinition},timeInForce:{location:{bitOffset:176,byteOffset:22,mask:0},type:TimeInForceTypeDefinition},limitPrice:{location:{bitOffset:184,byteOffset:23,mask:0},type:LimitPriceTypeDefinition},stopPrice:{location:{bitOffset:248,byteOffset:31,mask:0},type:StopPriceTypeDefinition}},jsonConverter:undefined};
}

function MarketStatusResultTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:MarketOpenResponseRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function MarketOpenResponseRefTypeDefinition() {
    return {type:"pointer"as const,elementType:MarketOpenResponseTypeDefinition};
}

function MarketOpenResponseTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:8,byteLength:1},fields:{isMarketOpen:{location:{bitOffset:0,byteOffset:0,mask:1},type:boolTypeDefinition}},jsonConverter:undefined};
}

function LatestBarResultTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:BarResponseRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function BarResponseRefTypeDefinition() {
    return {type:"pointer"as const,elementType:BarResponseTypeDefinition};
}

function BarResponseTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:576,byteLength:72},fields:{symbol:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},o:{location:{bitOffset:32,byteOffset:4,mask:0},type:DecimalFloatTypeDefinition},h:{location:{bitOffset:104,byteOffset:13,mask:0},type:DecimalFloatTypeDefinition},l:{location:{bitOffset:176,byteOffset:22,mask:0},type:DecimalFloatTypeDefinition},c:{location:{bitOffset:248,byteOffset:31,mask:0},type:DecimalFloatTypeDefinition},v:{location:{bitOffset:320,byteOffset:40,mask:0},type:int53TypeDefinition},n:{location:{bitOffset:384,byteOffset:48,mask:0},type:int53TypeDefinition},vw:{location:{bitOffset:448,byteOffset:56,mask:0},type:DecimalFloatTypeDefinition},t:{location:{bitOffset:544,byteOffset:68,mask:0},type:ISODateTimeStringForMessagingRefTypeDefinition}},jsonConverter:undefined};
}

function ISODateTimeStringForMessagingRefTypeDefinition() {
    return {type:"pointer"as const,elementType:ISODateTimeStringForMessagingTypeDefinition};
}

function ISODateTimeStringForMessagingTypeDefinition() {
    return {type:"string"as const,count:{encodingType:"Int32"as const,location:{bitOffset:0,byteOffset:0,mask:0}},encoding:"Utf16"as const,maxCount:undefined};
}

function int53TypeDefinition() {
    return "Int53"as const;
}

function DecimalFloatTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:72,byteLength:9},fields:{mantissa:{location:{bitOffset:0,byteOffset:0,mask:0},type:int64TypeDefinition},exponent:{location:{bitOffset:64,byteOffset:8,mask:0},type:int8TypeDefinition}},jsonConverter:"DecimalFloatConverter"as const};
}

function int8TypeDefinition() {
    return "Int8"as const;
}

function LatestBarRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{symbols:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function QuoteResultTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityQuoteResponseRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function EquityQuoteResponseRefTypeDefinition() {
    return {type:"pointer"as const,elementType:EquityQuoteResponseTypeDefinition};
}

function EquityQuoteResponseTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:224,byteLength:28},fields:{symbol:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},ap:{location:{bitOffset:32,byteOffset:4,mask:0},type:DecimalFloatTypeDefinition},bp:{location:{bitOffset:104,byteOffset:13,mask:0},type:DecimalFloatTypeDefinition},t:{location:{bitOffset:192,byteOffset:24,mask:0},type:ISODateTimeStringForMessagingRefTypeDefinition}},jsonConverter:undefined};
}

function EquityQuoteRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{symbols:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function HistoricalBarResultTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:BarResponseRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function HistoricalBarRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:192,byteLength:24},fields:{symbols:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},startTime:{location:{bitOffset:32,byteOffset:4,mask:0},type:ISODateTimeStringForMessagingRefTypeDefinition},endTime:{location:{bitOffset:64,byteOffset:8,mask:0},type:ISODateTimeStringForMessagingRefTypeDefinition},limit:{location:{bitOffset:96,byteOffset:12,mask:0},type:int53TypeDefinition},timeframe:{location:{bitOffset:160,byteOffset:20,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function AccountSummaryResultTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:AccountSummaryUpdateRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function AccountSummaryUpdateRefTypeDefinition() {
    return {type:"pointer"as const,elementType:AccountSummaryUpdateTypeDefinition};
}

function AccountSummaryUpdateTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{updateType:{location:{bitOffset:0,byteOffset:0,mask:0},type:UpdateTypeTypeDefinition},payload:{location:{bitOffset:32,byteOffset:4,mask:0},type:AccountSummaryRefTypeDefinition}},jsonConverter:undefined};
}

function AccountSummaryRefTypeDefinition() {
    return {type:"pointer"as const,elementType:AccountSummaryTypeDefinition};
}

function AccountSummaryTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:248,byteLength:31},fields:{accountNumber:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},cash:{location:{bitOffset:32,byteOffset:4,mask:0},type:DecimalFloatTypeDefinition},buyingPower:{location:{bitOffset:104,byteOffset:13,mask:0},type:DecimalFloatTypeDefinition},portfolioValue:{location:{bitOffset:176,byteOffset:22,mask:0},type:DecimalFloatTypeDefinition}},jsonConverter:undefined};
}

function EquityInstrumentResultTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityInstrumentUpdateRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function EquityInstrumentUpdateRefTypeDefinition() {
    return {type:"pointer"as const,elementType:EquityInstrumentUpdateTypeDefinition};
}

function EquityInstrumentUpdateTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{payload:{location:{bitOffset:0,byteOffset:0,mask:0},type:InstrumentRefTypeDefinition}},jsonConverter:undefined};
}

function InstrumentRefTypeDefinition() {
    return {type:"pointer"as const,elementType:InstrumentTypeDefinition};
}

function InstrumentRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:96,byteLength:12},fields:{searchStr:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},symbols:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefListRef_ComWeareadaptiveGatewayAlpacaTypeDefinition},limit:{location:{bitOffset:64,byteOffset:8,mask:0},type:int32TypeDefinition}},jsonConverter:undefined};
}

function int32TypeDefinition() {
    return "Int32"as const;
}

function stringRefListRef_ComWeareadaptiveGatewayAlpacaTypeDefinition() {
    return {type:"pointer"as const,elementType:stringRefList_ComWeareadaptiveGatewayAlpacaTypeDefinition};
}

function stringRefList_ComWeareadaptiveGatewayAlpacaTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:32,byteLength:4},elementType:stringRefTypeDefinition,lengthEncoding:undefined,firstElementOffset:2,maxCount:undefined};
}

function PositionResultTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:PositionUpdateRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:EquityErrorRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function PositionUpdateRefTypeDefinition() {
    return {type:"pointer"as const,elementType:PositionUpdateTypeDefinition};
}

function PositionUpdateTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{updateType:{location:{bitOffset:0,byteOffset:0,mask:0},type:UpdateTypeTypeDefinition},payload:{location:{bitOffset:32,byteOffset:4,mask:0},type:EquityPositionRefTypeDefinition}},jsonConverter:undefined};
}

function EquityPositionRefTypeDefinition() {
    return {type:"pointer"as const,elementType:EquityPositionTypeDefinition};
}

function EquityPositionTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:848,byteLength:106},fields:{assetId:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},symbol:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition},exchange:{location:{bitOffset:64,byteOffset:8,mask:0},type:stringRefTypeDefinition},assetClass:{location:{bitOffset:96,byteOffset:12,mask:0},type:stringRefTypeDefinition},avgEntryPrice:{location:{bitOffset:128,byteOffset:16,mask:0},type:stringRefTypeDefinition},qty:{location:{bitOffset:160,byteOffset:20,mask:0},type:stringRefTypeDefinition},side:{location:{bitOffset:192,byteOffset:24,mask:0},type:PositionSideTypeDefinition},marketValue:{location:{bitOffset:200,byteOffset:25,mask:0},type:DecimalFloatTypeDefinition},costBasis:{location:{bitOffset:272,byteOffset:34,mask:0},type:DecimalFloatTypeDefinition},unrealizedPl:{location:{bitOffset:344,byteOffset:43,mask:0},type:DecimalFloatTypeDefinition},unrealizedPlpc:{location:{bitOffset:416,byteOffset:52,mask:0},type:DecimalFloatTypeDefinition},unrealizedIntradayPl:{location:{bitOffset:488,byteOffset:61,mask:0},type:DecimalFloatTypeDefinition},unrealizedIntradayPlpc:{location:{bitOffset:560,byteOffset:70,mask:0},type:DecimalFloatTypeDefinition},currentPrice:{location:{bitOffset:632,byteOffset:79,mask:0},type:DecimalFloatTypeDefinition},lastDayPrice:{location:{bitOffset:704,byteOffset:88,mask:0},type:DecimalFloatTypeDefinition},changeToday:{location:{bitOffset:776,byteOffset:97,mask:0},type:DecimalFloatTypeDefinition}},jsonConverter:undefined};
}

function PositionSideTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"Long"as const,value:BigInt("1")},{name:"Short"as const,value:BigInt("2")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function ExecutionResponseTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{trade:{location:{bitOffset:0,byteOffset:0,mask:0},type:TradeRefTypeDefinition}},jsonConverter:undefined};
}

function TradeRefTypeDefinition() {
    return {type:"pointer"as const,elementType:TradeTypeDefinition};
}

function TradeTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:384,byteLength:48},fields:{tradeId:{location:{bitOffset:0,byteOffset:0,mask:0},type:int64TypeDefinition},tradeName:{location:{bitOffset:64,byteOffset:8,mask:0},type:stringRefTypeDefinition},currencyPair:{location:{bitOffset:96,byteOffset:12,mask:0},type:stringRefTypeDefinition},notional:{location:{bitOffset:128,byteOffset:16,mask:0},type:QuantityTypeDefinition},dealtCurrency:{location:{bitOffset:192,byteOffset:24,mask:0},type:stringRefTypeDefinition},direction:{location:{bitOffset:224,byteOffset:28,mask:0},type:DirectionTypeDefinition},spotRate:{location:{bitOffset:232,byteOffset:29,mask:0},type:PriceTypeDefinition},status:{location:{bitOffset:296,byteOffset:37,mask:0},type:TradeStatusTypeDefinition},tradeDate:{location:{bitOffset:320,byteOffset:40,mask:0},type:LocalDateTypeDefinition},valueDate:{location:{bitOffset:352,byteOffset:44,mask:0},type:LocalDateTypeDefinition}},jsonConverter:undefined};
}

function LocalDateTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{year:{location:{bitOffset:0,byteOffset:0,mask:0},type:int16TypeDefinition},month:{location:{bitOffset:16,byteOffset:2,mask:0},type:int8TypeDefinition},day:{location:{bitOffset:24,byteOffset:3,mask:0},type:int8TypeDefinition}},jsonConverter:"LocalDateConverter"as const};
}

function int16TypeDefinition() {
    return "Int16"as const;
}

function TradeStatusTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"Pending"as const,value:BigInt("1")},{name:"Done"as const,value:BigInt("2")},{name:"Rejected"as const,value:BigInt("3")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function PriceTypeDefinition() {
    return "Float64"as const;
}

function ExecuteTradeRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:256,byteLength:32},fields:{currencyPair:{location:{bitOffset:0,byteOffset:0,mask:0},type:SymbolForMessagingRefTypeDefinition},spotRate:{location:{bitOffset:32,byteOffset:4,mask:0},type:PriceTypeDefinition},valueDate:{location:{bitOffset:96,byteOffset:12,mask:0},type:LocalDateTypeDefinition},direction:{location:{bitOffset:128,byteOffset:16,mask:0},type:DirectionTypeDefinition},notional:{location:{bitOffset:136,byteOffset:17,mask:0},type:QuantityTypeDefinition},dealtCurrency:{location:{bitOffset:224,byteOffset:28,mask:0},type:CurrencyRefTypeDefinition}},jsonConverter:undefined};
}

function CurrencyRefTypeDefinition() {
    return {type:"pointer"as const,elementType:CurrencyTypeDefinition};
}

function CurrencyTypeDefinition() {
    return {type:"string"as const,count:{encodingType:"Int32"as const,location:{bitOffset:0,byteOffset:0,mask:0}},encoding:"Utf16"as const,maxCount:undefined};
}

function SymbolForMessagingRefTypeDefinition() {
    return {type:"pointer"as const,elementType:SymbolForMessagingTypeDefinition};
}

function SymbolForMessagingTypeDefinition() {
    return {type:"string"as const,count:{encodingType:"Int32"as const,location:{bitOffset:0,byteOffset:0,mask:0}},encoding:"Utf16"as const,maxCount:undefined};
}

function RfqUpdateTypeDefinition() {
    return {type:"union"as const,cases:{rfqCreated:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:RfqBodyRefTypeDefinition}},quoteCreated:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:QuoteBodyRefTypeDefinition}},quoteQuoted:{tag:3,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:QuoteBodyRefTypeDefinition}},quotePassed:{tag:4,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:QuoteBodyRefTypeDefinition}},quoteAccepted:{tag:5,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:QuoteBodyRefTypeDefinition}},rfqClosed:{tag:6,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:RfqBodyRefTypeDefinition}},startOfStateOfTheWorld:{tag:7,payload:undefined},endOfStateOfTheWorld:{tag:8,payload:undefined}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function QuoteBodyRefTypeDefinition() {
    return {type:"pointer"as const,elementType:QuoteBodyTypeDefinition};
}

function QuoteBodyTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:128,byteLength:16},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:QuoteIdTypeDefinition},rfqId:{location:{bitOffset:32,byteOffset:4,mask:0},type:RfqIdTypeDefinition},dealerId:{location:{bitOffset:64,byteOffset:8,mask:0},type:DealerIdTypeDefinition},state:{location:{bitOffset:96,byteOffset:12,mask:0},type:QuoteStateRefTypeDefinition}},jsonConverter:undefined};
}

function QuoteStateRefTypeDefinition() {
    return {type:"pointer"as const,elementType:QuoteStateTypeDefinition};
}

function QuoteStateTypeDefinition() {
    return {type:"union"as const,cases:{pendingWithoutPrice:{tag:1,payload:undefined},pendingWithPrice:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:PriceTypeDefinition}},passed:{tag:3,payload:undefined},accepted:{tag:4,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:PriceTypeDefinition}},rejectedWithPrice:{tag:5,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:PriceTypeDefinition}},rejectedWithoutPrice:{tag:6,payload:undefined}},encodedLength:{bitLength:72,byteLength:9},isGrowable:false};
}

function DealerIdTypeDefinition() {
    return "Int32"as const;
}

function RfqIdTypeDefinition() {
    return "Int32"as const;
}

function QuoteIdTypeDefinition() {
    return "Int32"as const;
}

function RfqBodyRefTypeDefinition() {
    return {type:"pointer"as const,elementType:RfqBodyTypeDefinition};
}

function RfqBodyTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:256,byteLength:32},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:RfqIdTypeDefinition},instrumentId:{location:{bitOffset:32,byteOffset:4,mask:0},type:InstrumentIdTypeDefinition},quantity:{location:{bitOffset:64,byteOffset:8,mask:0},type:QuantityTypeDefinition},direction:{location:{bitOffset:128,byteOffset:16,mask:0},type:DirectionTypeDefinition},state:{location:{bitOffset:136,byteOffset:17,mask:0},type:RfqStateTypeDefinition},expirySecs:{location:{bitOffset:160,byteOffset:20,mask:0},type:int32TypeDefinition},creationTimestamp:{location:{bitOffset:192,byteOffset:24,mask:0},type:int64TypeDefinition}},jsonConverter:undefined};
}

function RfqStateTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"Open"as const,value:BigInt("1")},{name:"Expired"as const,value:BigInt("2")},{name:"Cancelled"as const,value:BigInt("3")},{name:"Closed"as const,value:BigInt("4")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function InstrumentIdTypeDefinition() {
    return "Int32"as const;
}

function AcceptQuoteResponseTypeDefinition() {
    return {type:"union"as const,cases:{ack:{tag:1,payload:undefined},nack:{tag:2,payload:undefined}},encodedLength:{bitLength:8,byteLength:1},isGrowable:false};
}

function AcceptQuoteRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{quoteId:{location:{bitOffset:0,byteOffset:0,mask:0},type:QuoteIdTypeDefinition}},jsonConverter:undefined};
}

function PassResponseTypeDefinition() {
    return {type:"union"as const,cases:{ack:{tag:1,payload:undefined},nack:{tag:2,payload:undefined}},encodedLength:{bitLength:8,byteLength:1},isGrowable:false};
}

function PassRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{quoteId:{location:{bitOffset:0,byteOffset:0,mask:0},type:QuoteIdTypeDefinition}},jsonConverter:undefined};
}

function QuoteResponseTypeDefinition() {
    return {type:"union"as const,cases:{ack:{tag:1,payload:undefined},nack:{tag:2,payload:undefined}},encodedLength:{bitLength:8,byteLength:1},isGrowable:false};
}

function QuoteRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:96,byteLength:12},fields:{quoteId:{location:{bitOffset:0,byteOffset:0,mask:0},type:QuoteIdTypeDefinition},price:{location:{bitOffset:32,byteOffset:4,mask:0},type:PriceTypeDefinition}},jsonConverter:undefined};
}

function CancelRfqRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{rfqId:{location:{bitOffset:0,byteOffset:0,mask:0},type:RfqIdTypeDefinition}},jsonConverter:undefined};
}

function CreateRfqResponseTypeDefinition() {
    return {type:"union"as const,cases:{ack:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:RfqIdTypeDefinition}},nack:{tag:2,payload:undefined}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function CreateRfqRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:192,byteLength:24},fields:{instrumentId:{location:{bitOffset:0,byteOffset:0,mask:0},type:InstrumentIdTypeDefinition},dealerIds:{location:{bitOffset:32,byteOffset:4,mask:0},type:DealerIdListRefTypeDefinition},quantity:{location:{bitOffset:64,byteOffset:8,mask:0},type:QuantityTypeDefinition},direction:{location:{bitOffset:128,byteOffset:16,mask:0},type:DirectionTypeDefinition},expirySecs:{location:{bitOffset:160,byteOffset:20,mask:0},type:int32TypeDefinition}},jsonConverter:undefined};
}

function DealerIdListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:DealerIdListTypeDefinition};
}

function DealerIdListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:32,byteLength:4},elementType:DealerIdTypeDefinition,lengthEncoding:undefined,firstElementOffset:2,maxCount:undefined};
}

function DealerUpdateTypeDefinition() {
    return {type:"union"as const,cases:{added:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:DealerBodyRefTypeDefinition}},removed:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:DealerIdTypeDefinition}},startOfStateOfTheWorld:{tag:3,payload:undefined},endOfStateOfTheWorld:{tag:4,payload:undefined}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function DealerBodyRefTypeDefinition() {
    return {type:"pointer"as const,elementType:DealerBodyTypeDefinition};
}

function DealerBodyTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:DealerIdTypeDefinition},name:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function InstrumentUpdateTypeDefinition() {
    return {type:"union"as const,cases:{added:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:InstrumentBodyRefTypeDefinition}},removed:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:InstrumentIdTypeDefinition}},startOfStateOfTheWorld:{tag:3,payload:undefined},endOfStateOfTheWorld:{tag:4,payload:undefined}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function InstrumentBodyRefTypeDefinition() {
    return {type:"pointer"as const,elementType:InstrumentBodyTypeDefinition};
}

function InstrumentBodyTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:256,byteLength:32},fields:{id:{location:{bitOffset:0,byteOffset:0,mask:0},type:InstrumentIdTypeDefinition},name:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition},cusip:{location:{bitOffset:64,byteOffset:8,mask:0},type:stringRefTypeDefinition},ticker:{location:{bitOffset:96,byteOffset:12,mask:0},type:stringRefTypeDefinition},maturity:{location:{bitOffset:128,byteOffset:16,mask:0},type:LocalDateTypeDefinition},interestRate:{location:{bitOffset:160,byteOffset:20,mask:0},type:float64TypeDefinition},benchmark:{location:{bitOffset:224,byteOffset:28,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function float64TypeDefinition() {
    return "Float64"as const;
}

function EchoResponseTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{payload:{location:{bitOffset:0,byteOffset:0,mask:0},type:int32TypeDefinition}},jsonConverter:undefined};
}

function EchoRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{payload:{location:{bitOffset:0,byteOffset:0,mask:0},type:int32TypeDefinition}},jsonConverter:undefined};
}

function SetThroughputRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{targetUpdatesPerSecond:{location:{bitOffset:0,byteOffset:0,mask:0},type:int32TypeDefinition}},jsonConverter:undefined};
}

function GetThroughputResponseTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{updatesPerSecond:{location:{bitOffset:0,byteOffset:0,mask:0},type:int32TypeDefinition}},jsonConverter:undefined};
}

function CurrencyPairUpdatesTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:40,byteLength:5},fields:{updates:{location:{bitOffset:0,byteOffset:0,mask:0},type:CurrencyPairUpdateListRefTypeDefinition},isStateOfTheWorld:{location:{bitOffset:32,byteOffset:4,mask:1},type:boolTypeDefinition},isStale:{location:{bitOffset:33,byteOffset:4,mask:2},type:boolTypeDefinition}},jsonConverter:undefined};
}

function CurrencyPairUpdateListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:CurrencyPairUpdateListTypeDefinition};
}

function CurrencyPairUpdateListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:40,byteLength:5},elementType:CurrencyPairUpdateTypeDefinition,lengthEncoding:{encodingType:"UInt24"as const,location:{bitOffset:16,byteOffset:2,mask:0}},firstElementOffset:5,maxCount:undefined};
}

function CurrencyPairUpdateTypeDefinition() {
    return {type:"union"as const,cases:{added:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:CurrencyPairRefTypeDefinition}},removed:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:CurrencyPairRefTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function CurrencyPairRefTypeDefinition() {
    return {type:"pointer"as const,elementType:CurrencyPairTypeDefinition};
}

function CurrencyPairTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:96,byteLength:12},fields:{symbol:{location:{bitOffset:0,byteOffset:0,mask:0},type:SymbolForMessagingRefTypeDefinition},ratePrecision:{location:{bitOffset:32,byteOffset:4,mask:0},type:int32TypeDefinition},pipsPosition:{location:{bitOffset:64,byteOffset:8,mask:0},type:int32TypeDefinition}},jsonConverter:undefined};
}

function PriceTickHistoryTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{prices:{location:{bitOffset:0,byteOffset:0,mask:0},type:PriceTickListRefTypeDefinition}},jsonConverter:undefined};
}

function PriceTickListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:PriceTickListTypeDefinition};
}

function PriceTickListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:320,byteLength:40},elementType:PriceTickTypeDefinition,lengthEncoding:{encodingType:"UInt24"as const,location:{bitOffset:16,byteOffset:2,mask:0}},firstElementOffset:5,maxCount:undefined};
}

function PriceTickTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:320,byteLength:40},fields:{symbol:{location:{bitOffset:0,byteOffset:0,mask:0},type:SymbolForMessagingRefTypeDefinition},bid:{location:{bitOffset:32,byteOffset:4,mask:0},type:PriceTypeDefinition},ask:{location:{bitOffset:96,byteOffset:12,mask:0},type:PriceTypeDefinition},mid:{location:{bitOffset:160,byteOffset:20,mask:0},type:PriceTypeDefinition},valueDate:{location:{bitOffset:224,byteOffset:28,mask:0},type:LocalDateTypeDefinition},creationTimestamp:{location:{bitOffset:256,byteOffset:32,mask:0},type:int64TypeDefinition}},jsonConverter:undefined};
}

function PriceStreamRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{symbol:{location:{bitOffset:0,byteOffset:0,mask:0},type:SymbolForMessagingRefTypeDefinition}},jsonConverter:undefined};
}

function TradeUpdatesTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:40,byteLength:5},fields:{updates:{location:{bitOffset:0,byteOffset:0,mask:0},type:TradeListRefTypeDefinition},isStateOfTheWorld:{location:{bitOffset:32,byteOffset:4,mask:1},type:boolTypeDefinition},isStale:{location:{bitOffset:33,byteOffset:4,mask:2},type:boolTypeDefinition}},jsonConverter:undefined};
}

function TradeListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:TradeListTypeDefinition};
}

function TradeListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:384,byteLength:48},elementType:TradeTypeDefinition,lengthEncoding:{encodingType:"UInt24"as const,location:{bitOffset:16,byteOffset:2,mask:0}},firstElementOffset:5,maxCount:undefined};
}

function LogoutResponseTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:undefined}},encodedLength:{bitLength:8,byteLength:1},isGrowable:false};
}

function LoginResponseTypeDefinition() {
    return {type:"union"as const,cases:{success:{tag:1,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:LoginSuccessRefTypeDefinition}},error:{tag:2,payload:{location:{bitOffset:8,byteOffset:1,mask:0},type:LoginErrorTypeDefinition}}},encodedLength:{bitLength:40,byteLength:5},isGrowable:false};
}

function LoginErrorTypeDefinition() {
    return {type:"enum"as const,cases:[{name:"InvalidCredentials"as const,value:BigInt("1")},{name:"InternalError"as const,value:BigInt("2")},{name:"ServiceUnavailable"as const,value:BigInt("3")}],description:""as const,encoding:"Int8"as const,unknownCaseValue:undefined};
}

function LoginSuccessRefTypeDefinition() {
    return {type:"pointer"as const,elementType:LoginSuccessTypeDefinition};
}

function LoginSuccessTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{username:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},accountId:{location:{bitOffset:32,byteOffset:4,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function LoginRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{username:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition}},jsonConverter:undefined};
}

function PositionUpdatesTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:64,byteLength:8},fields:{currentPositions:{location:{bitOffset:0,byteOffset:0,mask:0},type:CurrencyPairPositionListRefTypeDefinition},history:{location:{bitOffset:32,byteOffset:4,mask:0},type:HistoricPositionListRefTypeDefinition}},jsonConverter:undefined};
}

function HistoricPositionListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:HistoricPositionListTypeDefinition};
}

function HistoricPositionListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:96,byteLength:12},elementType:HistoricPositionTypeDefinition,lengthEncoding:{encodingType:"UInt24"as const,location:{bitOffset:16,byteOffset:2,mask:0}},firstElementOffset:5,maxCount:undefined};
}

function HistoricPositionTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:96,byteLength:12},fields:{timestamp:{location:{bitOffset:0,byteOffset:0,mask:0},type:stringRefTypeDefinition},usdPnl:{location:{bitOffset:32,byteOffset:4,mask:0},type:float64TypeDefinition}},jsonConverter:undefined};
}

function CurrencyPairPositionListRefTypeDefinition() {
    return {type:"pointer"as const,elementType:CurrencyPairPositionListTypeDefinition};
}

function CurrencyPairPositionListTypeDefinition() {
    return {type:"list"as const,count:{encodingType:"Int16"as const,location:{bitOffset:0,byteOffset:0,mask:0}},elementLength:{bitLength:224,byteLength:28},elementType:CurrencyPairPositionTypeDefinition,lengthEncoding:{encodingType:"UInt24"as const,location:{bitOffset:16,byteOffset:2,mask:0}},firstElementOffset:5,maxCount:undefined};
}

function CurrencyPairPositionTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:224,byteLength:28},fields:{symbol:{location:{bitOffset:0,byteOffset:0,mask:0},type:SymbolForMessagingRefTypeDefinition},basePnl:{location:{bitOffset:32,byteOffset:4,mask:0},type:float64TypeDefinition},baseTradedAmount:{location:{bitOffset:96,byteOffset:12,mask:0},type:float64TypeDefinition},counterTradedAmount:{location:{bitOffset:160,byteOffset:20,mask:0},type:float64TypeDefinition}},jsonConverter:undefined};
}

function AnalyticsRequestTypeDefinition() {
    return {type:"record"as const,encodedLength:{bitLength:32,byteLength:4},fields:{currency:{location:{bitOffset:0,byteOffset:0,mask:0},type:CurrencyRefTypeDefinition}},jsonConverter:undefined};
}

export interface IAnalyticsService {
    getAnalytics(input: AnalyticsRequest): Observable<PositionUpdates>;
}

export interface IAuthService {
    login(input: LoginRequest): Observable<LoginResponse>;
    logout(): Observable<LogoutResponse>;
}

export interface IBlotterService {
    getTradeStream(): Observable<TradeUpdates>;
}

export interface IPricingService {
    getPriceUpdates(input: PriceStreamRequest): Observable<PriceTick>;
    getPriceHistory(input: PriceStreamRequest): Observable<PriceTickHistory>;
}

export interface IReferenceDataService {
    getCcyPairs(): Observable<CurrencyPairUpdates>;
}

export interface IThroughputAdminService {
    getThroughput(): Observable<GetThroughputResponse>;
    setThroughput(input: SetThroughputRequest): Observable<void>;
}

export interface IEchoService {
    echo(input: EchoRequest): Observable<EchoResponse>;
}

export interface IInstrumentService {
    subscribe(): Observable<InstrumentUpdate>;
}

export interface IDealerService {
    subscribe(): Observable<DealerUpdate>;
}

export interface IWorkflowService {
    createRfq(input: CreateRfqRequest): Observable<CreateRfqResponse>;
    cancelRfq(input: CancelRfqRequest): Observable<void>;
    quote(input: QuoteRequest): Observable<QuoteResponse>;
    pass(input: PassRequest): Observable<PassResponse>;
    accept(input: AcceptQuoteRequest): Observable<AcceptQuoteResponse>;
    subscribe(): Observable<RfqUpdate>;
}

export interface IExecutionService {
    executeTrade(input: ExecuteTradeRequest): Observable<ExecutionResponse>;
}

export interface IPositionService {
    getPositions(): Observable<PositionResult>;
}

export interface IEquityInstrumentService {
    getInstruments(input: InstrumentRequest): Observable<EquityInstrumentResult>;
}

export interface IAccountService {
    getAccountSummary(): Observable<AccountSummaryResult>;
}

export interface IMarketDataService {
    getHistoricalBars(input: HistoricalBarRequest): Observable<HistoricalBarResult>;
    getLatestQuote(input: EquityQuoteRequest): Observable<QuoteResult>;
    getLatestBars(input: LatestBarRequest): Observable<LatestBarResult>;
    subscribeToMarketStatus(): Observable<MarketStatusResult>;
}

export interface IEquityOrderService {
    createOrder(input: PlaceEquityOrderRequest): Observable<EquityOrderResponse>;
    updateOrder(input: UpdateEquityOrderRequest): Observable<EquityOrderResponse>;
    getOrders(): Observable<EquityOrderUpdateResult>;
    getOrderById(input: OrderId): Observable<EquityOrderResponse>;
    cancelOrder(input: OrderId): Observable<EquityOrderAckResponse>;
}

export interface IWatchListService {
    getWatchListsByAccountId(): Observable<GetWatchListsByAccountIdResponse>;
    getWatchListById(input: WatchListId): Observable<WatchListResponse>;
    createWatchList(input: CreateWatchListRequest): Observable<WatchListResponse>;
    addAssetToWatchList(input: AddOrRemoveAssetRequest): Observable<WatchListResponse>;
    updateWatchList(input: UpdateWatchListRequest): Observable<WatchListResponse>;
    deleteWatchList(input: WatchListId): Observable<WatchListAckResponse>;
    removeAssetFromWatchList(input: AddOrRemoveAssetRequest): Observable<WatchListAckResponse>;
}

export class AnalyticsService implements IAnalyticsService {
    getAnalytics: (input: AnalyticsRequest) => Observable<PositionUpdates> = (input) => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'AnalyticsService',
                serviceVersion: 'ZPWRZEYxeuriphf5mM3F-3ajfVE=',
                majorVersionContentAddress: '0xe62e7df2d92678862813a264ddc89f1027ee5a31',
                methodName: 'getAnalytics',
                inboundStream: 'one',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("398568636411697713"),
                        COMPLETED: BigInt("542683824487553585"),
                        ERROR: BigInt("110338260259985969"),
                        CANCEL: BigInt("254453448335841841")
                    },
                    response: {
                        NEXT: BigInt("290482514890006657"),
                        COMPLETED: BigInt("434597702965862529"),
                        ERROR: BigInt("2252138738294913"),
                        CANCEL: BigInt("146367326814150785")
                    }
                },
                annotations: []
            },allocators.responseAllocator(PositionUpdatesTypeDefinition),allocators.requestAllocator(input, AnalyticsRequestTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getAnalytics(input: AnalyticsRequest): Observable<PositionUpdates> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.analyticsService.getAnalytics(input)));
    }
}

export class AuthService implements IAuthService {
    login: (input: LoginRequest) => Observable<LoginResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'AuthService',
                serviceVersion: '9b68JmYcj4kmP62clQzyGQSV5YI=',
                majorVersionContentAddress: '0x0132e1174f9cc8b56236743323aa06bbfde4275c',
                methodName: 'login',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("351281578353370972"),
                        COMPLETED: BigInt("495396766429226844"),
                        ERROR: BigInt("63051202201659228"),
                        CANCEL: BigInt("207166390277515100")
                    },
                    response: {
                        NEXT: BigInt("340022371718858839"),
                        COMPLETED: BigInt("484137559794714711"),
                        ERROR: BigInt("51791995567147095"),
                        CANCEL: BigInt("195907183643002967")
                    }
                },
                annotations: []
            },allocators.responseAllocator(LoginResponseTypeDefinition),allocators.requestAllocator(input, LoginRequestTypeDefinition));
        };
    logout: () => Observable<LogoutResponse> = () => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'AuthService',
                serviceVersion: '9b68JmYcj4kmP62clQzyGQSV5YI=',
                majorVersionContentAddress: '0x7f740cf7d54cce9359b65389116473b18883c81c',
                methodName: 'logout',
                inboundStream: 'empty',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("351281533434447900"),
                        COMPLETED: BigInt("495396721510303772"),
                        ERROR: BigInt("63051157282736156"),
                        CANCEL: BigInt("207166345358592028")
                    },
                    response: {
                        NEXT: BigInt("430094553658873663"),
                        COMPLETED: BigInt("574209741734729535"),
                        ERROR: BigInt("141864177507161919"),
                        CANCEL: BigInt("285979365583017791")
                    }
                },
                annotations: []
            },allocators.responseAllocator(LogoutResponseTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static login(input: LoginRequest): Observable<LoginResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.authService.login(input)));
    }

    static logout(): Observable<LogoutResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.authService.logout()));
    }
}

export class BlotterService implements IBlotterService {
    getTradeStream: () => Observable<TradeUpdates> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'BlotterService',
                serviceVersion: '71ZHSzqYkzeAMY8FHL-e1ZjDX9M=',
                majorVersionContentAddress: '0x4f8899fe381a704fbaed510e1751f1b86d0c8c90',
                methodName: 'getTradeStream',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("324259965274197136"),
                        COMPLETED: BigInt("468375153350053008"),
                        ERROR: BigInt("36029589122485392"),
                        CANCEL: BigInt("180144777198341264")
                    },
                    response: {
                        NEXT: BigInt("333266776337026836"),
                        COMPLETED: BigInt("477381964412882708"),
                        ERROR: BigInt("45036400185315092"),
                        CANCEL: BigInt("189151588261170964")
                    }
                },
                annotations: []
            },allocators.responseAllocator(TradeUpdatesTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getTradeStream(): Observable<TradeUpdates> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.blotterService.getTradeStream()));
    }
}

export class PricingService implements IPricingService {
    getPriceUpdates: (input: PriceStreamRequest) => Observable<PriceTick> = (input) => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'PricingService',
                serviceVersion: 'lMGjEAXz4GnWgyQK2jJTCMe1h68=',
                majorVersionContentAddress: '0xd47e9b02537be1029246d28fad3846ab88f26ebe',
                methodName: 'getPriceUpdates',
                inboundStream: 'one',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("427842701337194174"),
                        COMPLETED: BigInt("571957889413050046"),
                        ERROR: BigInt("139612325185482430"),
                        CANCEL: BigInt("283727513261338302")
                    },
                    response: {
                        NEXT: BigInt("342274080496420888"),
                        COMPLETED: BigInt("486389268572276760"),
                        ERROR: BigInt("54043704344709144"),
                        CANCEL: BigInt("198158892420565016")
                    }
                },
                annotations: []
            },allocators.responseAllocator(PriceTickTypeDefinition),allocators.requestAllocator(input, PriceStreamRequestTypeDefinition));
        };
    getPriceHistory: (input: PriceStreamRequest) => Observable<PriceTickHistory> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'PricingService',
                serviceVersion: 'lMGjEAXz4GnWgyQK2jJTCMe1h68=',
                majorVersionContentAddress: '0xa1ed267eed573cf8a11410c6420e05a2db1cc99c',
                methodName: 'getPriceHistory',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("351281470395697564"),
                        COMPLETED: BigInt("495396658471553436"),
                        ERROR: BigInt("63051094243985820"),
                        CANCEL: BigInt("207166282319841692")
                    },
                    response: {
                        NEXT: BigInt("310748720041365834"),
                        COMPLETED: BigInt("454863908117221706"),
                        ERROR: BigInt("22518343889654090"),
                        CANCEL: BigInt("166633531965509962")
                    }
                },
                annotations: []
            },allocators.responseAllocator(PriceTickHistoryTypeDefinition),allocators.requestAllocator(input, PriceStreamRequestTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getPriceUpdates(input: PriceStreamRequest): Observable<PriceTick> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.pricingService.getPriceUpdates(input)));
    }

    static getPriceHistory(input: PriceStreamRequest): Observable<PriceTickHistory> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.pricingService.getPriceHistory(input)));
    }
}

export class ReferenceDataService implements IReferenceDataService {
    getCcyPairs: () => Observable<CurrencyPairUpdates> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'ReferenceDataService',
                serviceVersion: 'OqWWP0EWma-LLq1gVKo89v7EEDU=',
                majorVersionContentAddress: '0xe61a79918fd439042742d78a7b67ccce35e745c6',
                methodName: 'getCcyPairs',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("301742060701435334"),
                        COMPLETED: BigInt("445857248777291206"),
                        ERROR: BigInt("13511684549723590"),
                        CANCEL: BigInt("157626872625579462")
                    },
                    response: {
                        NEXT: BigInt("425590888427486909"),
                        COMPLETED: BigInt("569706076503342781"),
                        ERROR: BigInt("137360512275775165"),
                        CANCEL: BigInt("281475700351631037")
                    }
                },
                annotations: []
            },allocators.responseAllocator(CurrencyPairUpdatesTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getCcyPairs(): Observable<CurrencyPairUpdates> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.referenceDataService.getCcyPairs()));
    }
}

export class ThroughputAdminService implements IThroughputAdminService {
    getThroughput: () => Observable<GetThroughputResponse> = () => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'ThroughputAdminService',
                serviceVersion: 'hPl5jJ6kWETbXFuLnvWAwrfi5jc=',
                majorVersionContentAddress: '0x98ab50219a2ea5d9e19eb25f691bdbb34b3657dc',
                methodName: 'getThroughput',
                inboundStream: 'empty',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("351281540995897308"),
                        COMPLETED: BigInt("495396729071753180"),
                        ERROR: BigInt("63051164844185564"),
                        CANCEL: BigInt("207166352920041436")
                    },
                    response: {
                        NEXT: BigInt("317504247172331149"),
                        COMPLETED: BigInt("461619435248187021"),
                        ERROR: BigInt("29273871020619405"),
                        CANCEL: BigInt("173389059096475277")
                    }
                },
                annotations: []
            },allocators.responseAllocator(GetThroughputResponseTypeDefinition));
        };
    setThroughput: (input: SetThroughputRequest) => Observable<void> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'ThroughputAdminService',
                serviceVersion: 'hPl5jJ6kWETbXFuLnvWAwrfi5jc=',
                majorVersionContentAddress: '0xec5a6ccffbbc97f1281f69833904dff1deb345be',
                methodName: 'setThroughput',
                inboundStream: 'one',
                outboundStream: 'empty',
                routingData: {
                    request: {
                        NEXT: BigInt("427843003423606206"),
                        COMPLETED: BigInt("571958191499462078"),
                        ERROR: BigInt("139612627271894462"),
                        CANCEL: BigInt("283727815347750334")
                    },
                    response: {
                        NEXT: BigInt("400820774001594930"),
                        COMPLETED: BigInt("544935962077450802"),
                        ERROR: BigInt("112590397849883186"),
                        CANCEL: BigInt("256705585925739058")
                    }
                },
                annotations: []
            },undefined,allocators.requestAllocator(input, SetThroughputRequestTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getThroughput(): Observable<GetThroughputResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.throughputAdminService.getThroughput()));
    }

    static setThroughput(input: SetThroughputRequest): Observable<void> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.throughputAdminService.setThroughput(input)));
    }
}

export class EchoService implements IEchoService {
    echo: (input: EchoRequest) => Observable<EchoResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'EchoService',
                serviceVersion: '3pvE2P7uCShir_5g26nbD2Q4pSI=',
                majorVersionContentAddress: '0xf49cc1a0c1899403dd3d5d414eef4ce7dd9d2316',
                methodName: 'echo',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("337770967908295446"),
                        COMPLETED: BigInt("481886155984151318"),
                        ERROR: BigInt("49540591756583702"),
                        CANCEL: BigInt("193655779832439574")
                    },
                    response: {
                        NEXT: BigInt("398569432155476081"),
                        COMPLETED: BigInt("542684620231331953"),
                        ERROR: BigInt("110339056003764337"),
                        CANCEL: BigInt("254454244079620209")
                    }
                },
                annotations: []
            },allocators.responseAllocator(EchoResponseTypeDefinition),allocators.requestAllocator(input, EchoRequestTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static echo(input: EchoRequest): Observable<EchoResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.echoService.echo(input)));
    }
}

export class InstrumentService implements IInstrumentService {
    subscribe: () => Observable<InstrumentUpdate> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'InstrumentService',
                serviceVersion: 'F_OPT_bs_UpYBTnuGcirYfGLd08=',
                majorVersionContentAddress: '0x3f05c6b0f369625ad6cff51ef7e574ea07335c0f',
                methodName: 'subscribe',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("322008378500144143"),
                        COMPLETED: BigInt("466123566576000015"),
                        ERROR: BigInt("33778002348432399"),
                        CANCEL: BigInt("177893190424288271")
                    },
                    response: {
                        NEXT: BigInt("288230663845826368"),
                        COMPLETED: BigInt("432345851921682240"),
                        ERROR: BigInt("287694114624"),
                        CANCEL: BigInt("144115475769970496")
                    }
                },
                annotations: []
            },allocators.responseAllocator(InstrumentUpdateTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static subscribe(): Observable<InstrumentUpdate> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.instrumentService.subscribe()));
    }
}

export class DealerService implements IDealerService {
    subscribe: () => Observable<DealerUpdate> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'DealerService',
                serviceVersion: 'H_zyq8pnacrwFlPwJjz2Fb1788M=',
                majorVersionContentAddress: '0x11710ad71d1ec5d9fdd981e2dd608084bdede196',
                methodName: 'subscribe',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("337770542174953878"),
                        COMPLETED: BigInt("481885730250809750"),
                        ERROR: BigInt("49540166023242134"),
                        CANCEL: BigInt("193655354099098006")
                    },
                    response: {
                        NEXT: BigInt("328763641913654098"),
                        COMPLETED: BigInt("472878829989509970"),
                        ERROR: BigInt("40533265761942354"),
                        CANCEL: BigInt("184648453837798226")
                    }
                },
                annotations: []
            },allocators.responseAllocator(DealerUpdateTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static subscribe(): Observable<DealerUpdate> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.dealerService.subscribe()));
    }
}

export class WorkflowService implements IWorkflowService {
    createRfq: (input: CreateRfqRequest) => Observable<CreateRfqResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WorkflowService',
                serviceVersion: 'pMWZC7BYrwInJjqMJtcfKgtCgtY=',
                majorVersionContentAddress: '0x5d266533fe0016e5a2e6d9cfc8012558d48ccd0d',
                methodName: 'createRfq',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("317504155252739341"),
                        COMPLETED: BigInt("461619343328595213"),
                        ERROR: BigInt("29273779101027597"),
                        CANCEL: BigInt("173388967176883469")
                    },
                    response: {
                        NEXT: BigInt("425590253033938557"),
                        COMPLETED: BigInt("569705441109794429"),
                        ERROR: BigInt("137359876882226813"),
                        CANCEL: BigInt("281475064958082685")
                    }
                },
                annotations: []
            },allocators.responseAllocator(CreateRfqResponseTypeDefinition),allocators.requestAllocator(input, CreateRfqRequestTypeDefinition));
        };
    cancelRfq: (input: CancelRfqRequest) => Observable<void> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WorkflowService',
                serviceVersion: 'pMWZC7BYrwInJjqMJtcfKgtCgtY=',
                majorVersionContentAddress: '0x462a1441fbf810fe69b66cd4cc23bf7e919a3367',
                methodName: 'cancelRfq',
                inboundStream: 'one',
                outboundStream: 'empty',
                routingData: {
                    request: {
                        NEXT: BigInt("376051112494117735"),
                        COMPLETED: BigInt("520166300569973607"),
                        ERROR: BigInt("87820736342405991"),
                        CANCEL: BigInt("231935924418261863")
                    },
                    response: {
                        NEXT: BigInt("315252471731274636"),
                        COMPLETED: BigInt("459367659807130508"),
                        ERROR: BigInt("27022095579562892"),
                        CANCEL: BigInt("171137283655418764")
                    }
                },
                annotations: []
            },undefined,allocators.requestAllocator(input, CancelRfqRequestTypeDefinition));
        };
    quote: (input: QuoteRequest) => Observable<QuoteResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WorkflowService',
                serviceVersion: 'pMWZC7BYrwInJjqMJtcfKgtCgtY=',
                majorVersionContentAddress: '0xdc9486caef0addc7d7cd8915ac2a5c4d9ccd09f3',
                methodName: 'quote',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("403072499992824307"),
                        COMPLETED: BigInt("547187688068680179"),
                        ERROR: BigInt("114842123841112563"),
                        CANCEL: BigInt("258957311916968435")
                    },
                    response: {
                        NEXT: BigInt("349029131811972635"),
                        COMPLETED: BigInt("493144319887828507"),
                        ERROR: BigInt("60798755660260891"),
                        CANCEL: BigInt("204913943736116763")
                    }
                },
                annotations: []
            },allocators.responseAllocator(QuoteResponseTypeDefinition),allocators.requestAllocator(input, QuoteRequestTypeDefinition));
        };
    pass: (input: PassRequest) => Observable<PassResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WorkflowService',
                serviceVersion: 'pMWZC7BYrwInJjqMJtcfKgtCgtY=',
                majorVersionContentAddress: '0x6af04e7fee4e349d520228e951fa95d29734945d',
                methodName: 'pass',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("353533475228521565"),
                        COMPLETED: BigInt("497648663304377437"),
                        ERROR: BigInt("65303099076809821"),
                        CANCEL: BigInt("209418287152665693")
                    },
                    response: {
                        NEXT: BigInt("306245164191815432"),
                        COMPLETED: BigInt("450360352267671304"),
                        ERROR: BigInt("18014788040103688"),
                        CANCEL: BigInt("162129976115959560")
                    }
                },
                annotations: []
            },allocators.responseAllocator(PassResponseTypeDefinition),allocators.requestAllocator(input, PassRequestTypeDefinition));
        };
    accept: (input: AcceptQuoteRequest) => Observable<AcceptQuoteResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WorkflowService',
                serviceVersion: 'pMWZC7BYrwInJjqMJtcfKgtCgtY=',
                majorVersionContentAddress: '0xf19a7b5f8016848ea2aaa666c0b61a96d5c48172',
                methodName: 'accept',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("400821014667493746"),
                        COMPLETED: BigInt("544936202743349618"),
                        ERROR: BigInt("112590638515782002"),
                        CANCEL: BigInt("256705826591637874")
                    },
                    response: {
                        NEXT: BigInt("364791847448101730"),
                        COMPLETED: BigInt("508907035523957602"),
                        ERROR: BigInt("76561471296389986"),
                        CANCEL: BigInt("220676659372245858")
                    }
                },
                annotations: []
            },allocators.responseAllocator(AcceptQuoteResponseTypeDefinition),allocators.requestAllocator(input, AcceptQuoteRequestTypeDefinition));
        };
    subscribe: () => Observable<RfqUpdate> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'WorkflowService',
                serviceVersion: 'pMWZC7BYrwInJjqMJtcfKgtCgtY=',
                majorVersionContentAddress: '0xe366a125e0df38e5281cb702f51fefb02f82cb20',
                methodName: 'subscribe',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("360288726900984608"),
                        COMPLETED: BigInt("504403914976840480"),
                        ERROR: BigInt("72058350749272864"),
                        CANCEL: BigInt("216173538825128736")
                    },
                    response: {
                        NEXT: BigInt("371547169517076709"),
                        COMPLETED: BigInt("515662357592932581"),
                        ERROR: BigInt("83316793365364965"),
                        CANCEL: BigInt("227431981441220837")
                    }
                },
                annotations: []
            },allocators.responseAllocator(RfqUpdateTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static createRfq(input: CreateRfqRequest): Observable<CreateRfqResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.workflowService.createRfq(input)));
    }

    static cancelRfq(input: CancelRfqRequest): Observable<void> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.workflowService.cancelRfq(input)));
    }

    static quote(input: QuoteRequest): Observable<QuoteResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.workflowService.quote(input)));
    }

    static pass(input: PassRequest): Observable<PassResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.workflowService.pass(input)));
    }

    static accept(input: AcceptQuoteRequest): Observable<AcceptQuoteResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.workflowService.accept(input)));
    }

    static subscribe(): Observable<RfqUpdate> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.workflowService.subscribe()));
    }
}

export class ExecutionService implements IExecutionService {
    executeTrade: (input: ExecuteTradeRequest) => Observable<ExecutionResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'ExecutionService',
                serviceVersion: 'u9AuEKVxDfRiwjRAJX0iJJ9v5cw=',
                majorVersionContentAddress: '0xcff73f76c021c915bfe397253bda70b7bb287597',
                methodName: 'executeTrade',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("340022560985478551"),
                        COMPLETED: BigInt("484137749061334423"),
                        ERROR: BigInt("51792184833766807"),
                        CANCEL: BigInt("195907372909622679")
                    },
                    response: {
                        NEXT: BigInt("405324425429920820"),
                        COMPLETED: BigInt("549439613505776692"),
                        ERROR: BigInt("117094049278209076"),
                        CANCEL: BigInt("261209237354064948")
                    }
                },
                annotations: []
            },allocators.responseAllocator(ExecutionResponseTypeDefinition),allocators.requestAllocator(input, ExecuteTradeRequestTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static executeTrade(input: ExecuteTradeRequest): Observable<ExecutionResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.executionService.executeTrade(input)));
    }
}

export class PositionService implements IPositionService {
    getPositions: () => Observable<PositionResult> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'PositionService',
                serviceVersion: 'b8YZ9BifaaGqaUZpclUb_gZcx5s=',
                majorVersionContentAddress: '0xcf849797b70c69c2dbdceb01358d36b4cbb0a18b',
                methodName: 'getPositions',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("313000950613713291"),
                        COMPLETED: BigInt("457116138689569163"),
                        ERROR: BigInt("24770574462001547"),
                        CANCEL: BigInt("168885762537857419")
                    },
                    response: {
                        NEXT: BigInt("360288244095957792"),
                        COMPLETED: BigInt("504403432171813664"),
                        ERROR: BigInt("72057867944246048"),
                        CANCEL: BigInt("216173056020101920")
                    }
                },
                annotations: []
            },allocators.responseAllocator(PositionResultTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getPositions(): Observable<PositionResult> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.positionService.getPositions()));
    }
}

export class EquityInstrumentService implements IEquityInstrumentService {
    getInstruments: (input: InstrumentRequest) => Observable<EquityInstrumentResult> = (input) => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'EquityInstrumentService',
                serviceVersion: '9ykBt_EhLR0E7ypLa_KAf1QpTvY=',
                majorVersionContentAddress: '0x42d9e21f47d202f7a98046d7085b92e730d50873',
                methodName: 'getInstruments',
                inboundStream: 'one',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("403073159606372467"),
                        COMPLETED: BigInt("547188347682228339"),
                        ERROR: BigInt("114842783454660723"),
                        CANCEL: BigInt("258957971530516595")
                    },
                    response: {
                        NEXT: BigInt("317504631728498381"),
                        COMPLETED: BigInt("461619819804354253"),
                        ERROR: BigInt("29274255576786637"),
                        CANCEL: BigInt("173389443652642509")
                    }
                },
                annotations: []
            },allocators.responseAllocator(EquityInstrumentResultTypeDefinition),allocators.requestAllocator(input, InstrumentRequestTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getInstruments(input: InstrumentRequest): Observable<EquityInstrumentResult> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.equityInstrumentService.getInstruments(input)));
    }
}

export class AccountService implements IAccountService {
    getAccountSummary: () => Observable<AccountSummaryResult> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'AccountService',
                serviceVersion: 'lnWI3AjUtLWJ0U0F_le4wk5WfrQ=',
                majorVersionContentAddress: '0x1bfc2e89d6837893e382f44fd2ea5d4d04ba332b',
                methodName: 'getAccountSummary',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("385058098931970859"),
                        COMPLETED: BigInt("529173287007826731"),
                        ERROR: BigInt("96827722780259115"),
                        CANCEL: BigInt("240942910856114987")
                    },
                    response: {
                        NEXT: BigInt("400821363052696626"),
                        COMPLETED: BigInt("544936551128552498"),
                        ERROR: BigInt("112590986900984882"),
                        CANCEL: BigInt("256706174976840754")
                    }
                },
                annotations: []
            },allocators.responseAllocator(AccountSummaryResultTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getAccountSummary(): Observable<AccountSummaryResult> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.accountService.getAccountSummary()));
    }
}

export class MarketDataService implements IMarketDataService {
    getHistoricalBars: (input: HistoricalBarRequest) => Observable<HistoricalBarResult> = (input) => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'MarketDataService',
                serviceVersion: '1VaofSweYwSUbMKO-Pqf6dTYNis=',
                majorVersionContentAddress: '0xa370cdec6482b487a58cd4f54b4bdbd63269e540',
                methodName: 'getHistoricalBars',
                inboundStream: 'one',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("288231296120513856"),
                        COMPLETED: BigInt("432346484196369728"),
                        ERROR: BigInt("919968802112"),
                        CANCEL: BigInt("144116108044657984")
                    },
                    response: {
                        NEXT: BigInt("294986016608632003"),
                        COMPLETED: BigInt("439101204684487875"),
                        ERROR: BigInt("6755640456920259"),
                        CANCEL: BigInt("150870828532776131")
                    }
                },
                annotations: []
            },allocators.responseAllocator(HistoricalBarResultTypeDefinition),allocators.requestAllocator(input, HistoricalBarRequestTypeDefinition));
        };
    getLatestQuote: (input: EquityQuoteRequest) => Observable<QuoteResult> = (input) => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'MarketDataService',
                serviceVersion: '1VaofSweYwSUbMKO-Pqf6dTYNis=',
                majorVersionContentAddress: '0x084e23fcc84bb222763ce037ba248c16ff000952',
                methodName: 'getLatestQuote',
                inboundStream: 'one',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("328762871565519186"),
                        COMPLETED: BigInt("472878059641375058"),
                        ERROR: BigInt("40532495413807442"),
                        CANCEL: BigInt("184647683489663314")
                    },
                    response: {
                        NEXT: BigInt("373799204686962214"),
                        COMPLETED: BigInt("517914392762818086"),
                        ERROR: BigInt("85568828535250470"),
                        CANCEL: BigInt("229684016611106342")
                    }
                },
                annotations: []
            },allocators.responseAllocator(QuoteResultTypeDefinition),allocators.requestAllocator(input, EquityQuoteRequestTypeDefinition));
        };
    getLatestBars: (input: LatestBarRequest) => Observable<LatestBarResult> = (input) => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'MarketDataService',
                serviceVersion: '1VaofSweYwSUbMKO-Pqf6dTYNis=',
                majorVersionContentAddress: '0x22e9529c0797aea2fb782f210597bc021b6731f5',
                methodName: 'getLatestBars',
                inboundStream: 'one',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("407575775326712309"),
                        COMPLETED: BigInt("551690963402568181"),
                        ERROR: BigInt("119345399175000565"),
                        CANCEL: BigInt("263460587250856437")
                    },
                    response: {
                        NEXT: BigInt("292734466802089282"),
                        COMPLETED: BigInt("436849654877945154"),
                        ERROR: BigInt("4504090650377538"),
                        CANCEL: BigInt("148619278726233410")
                    }
                },
                annotations: []
            },allocators.responseAllocator(LatestBarResultTypeDefinition),allocators.requestAllocator(input, LatestBarRequestTypeDefinition));
        };
    subscribeToMarketStatus: () => Observable<MarketStatusResult> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'MarketDataService',
                serviceVersion: '1VaofSweYwSUbMKO-Pqf6dTYNis=',
                majorVersionContentAddress: '0x4bb42a7c336d5f2155b2937b90c8baa4ec4c1bfc',
                methodName: 'subscribeToMarketStatus',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("423339073311874044"),
                        COMPLETED: BigInt("567454261387729916"),
                        ERROR: BigInt("135108697160162300"),
                        CANCEL: BigInt("279223885236018172")
                    },
                    response: {
                        NEXT: BigInt("290482792286832961"),
                        COMPLETED: BigInt("434597980362688833"),
                        ERROR: BigInt("2252416135121217"),
                        CANCEL: BigInt("146367604210977089")
                    }
                },
                annotations: []
            },allocators.responseAllocator(MarketStatusResultTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getHistoricalBars(input: HistoricalBarRequest): Observable<HistoricalBarResult> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.marketDataService.getHistoricalBars(input)));
    }

    static getLatestQuote(input: EquityQuoteRequest): Observable<QuoteResult> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.marketDataService.getLatestQuote(input)));
    }

    static getLatestBars(input: LatestBarRequest): Observable<LatestBarResult> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.marketDataService.getLatestBars(input)));
    }

    static subscribeToMarketStatus(): Observable<MarketStatusResult> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.marketDataService.subscribeToMarketStatus()));
    }
}

export class EquityOrderService implements IEquityOrderService {
    createOrder: (input: PlaceEquityOrderRequest) => Observable<EquityOrderResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'EquityOrderService',
                serviceVersion: 'Smd7cUsmlbK_Ny_7dRsL5PrtwRI=',
                majorVersionContentAddress: '0xf7dfedce22920c8b90b31eae8c36011557e61004',
                methodName: 'createOrder',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("297237667075461124"),
                        COMPLETED: BigInt("441352855151316996"),
                        ERROR: BigInt("9007290923749380"),
                        CANCEL: BigInt("153122478999605252")
                    },
                    response: {
                        NEXT: BigInt("322008285100014607"),
                        COMPLETED: BigInt("466123473175870479"),
                        ERROR: BigInt("33777908948302863"),
                        CANCEL: BigInt("177893097024158735")
                    }
                },
                annotations: []
            },allocators.responseAllocator(EquityOrderResponseTypeDefinition),allocators.requestAllocator(input, PlaceEquityOrderRequestTypeDefinition));
        };
    updateOrder: (input: UpdateEquityOrderRequest) => Observable<EquityOrderResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'EquityOrderService',
                serviceVersion: 'Smd7cUsmlbK_Ny_7dRsL5PrtwRI=',
                majorVersionContentAddress: '0xc189142320e96a9b2ec9e5e1a76927c938d6f933',
                methodName: 'updateOrder',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("403073030891698483"),
                        COMPLETED: BigInt("547188218967554355"),
                        ERROR: BigInt("114842654739986739"),
                        CANCEL: BigInt("258957842815842611")
                    },
                    response: {
                        NEXT: BigInt("367043467240668835"),
                        COMPLETED: BigInt("511158655316524707"),
                        ERROR: BigInt("78813091088957091"),
                        CANCEL: BigInt("222928279164812963")
                    }
                },
                annotations: []
            },allocators.responseAllocator(EquityOrderResponseTypeDefinition),allocators.requestAllocator(input, UpdateEquityOrderRequestTypeDefinition));
        };
    getOrders: () => Observable<EquityOrderUpdateResult> = () => {
         return this.hydraPlatform.requestStream$({
                serviceName: 'EquityOrderService',
                serviceVersion: 'Smd7cUsmlbK_Ny_7dRsL5PrtwRI=',
                majorVersionContentAddress: '0xda83ebe088ee9939f7efa08f8dcf20fce259779e',
                methodName: 'getOrders',
                inboundStream: 'empty',
                outboundStream: 'many',
                routingData: {
                    request: {
                        NEXT: BigInt("355785456691541918"),
                        COMPLETED: BigInt("499900644767397790"),
                        ERROR: BigInt("67555080539830174"),
                        CANCEL: BigInt("211670268615686046")
                    },
                    response: {
                        NEXT: BigInt("351281158522571548"),
                        COMPLETED: BigInt("495396346598427420"),
                        ERROR: BigInt("63050782370859804"),
                        CANCEL: BigInt("207165970446715676")
                    }
                },
                annotations: []
            },allocators.responseAllocator(EquityOrderUpdateResultTypeDefinition));
        };
    getOrderById: (input: OrderId) => Observable<EquityOrderResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'EquityOrderService',
                serviceVersion: 'Smd7cUsmlbK_Ny_7dRsL5PrtwRI=',
                majorVersionContentAddress: '0x244f4037af899ddd9773d3372da6ec478e4e9ed1',
                methodName: 'getOrderById',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("326511280314556113"),
                        COMPLETED: BigInt("470626468390411985"),
                        ERROR: BigInt("38280904162844369"),
                        CANCEL: BigInt("182396092238700241")
                    },
                    response: {
                        NEXT: BigInt("369296157517038244"),
                        COMPLETED: BigInt("513411345592894116"),
                        ERROR: BigInt("81065781365326500"),
                        CANCEL: BigInt("225180969441182372")
                    }
                },
                annotations: []
            },allocators.responseAllocator(EquityOrderResponseTypeDefinition),allocators.requestAllocator(input, OrderIdTypeDefinition));
        };
    cancelOrder: (input: OrderId) => Observable<EquityOrderAckResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'EquityOrderService',
                serviceVersion: 'Smd7cUsmlbK_Ny_7dRsL5PrtwRI=',
                majorVersionContentAddress: '0x211d8dfe7a4aceb709975c07ea41a095d52259b3',
                methodName: 'cancelOrder',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("403072810175584691"),
                        COMPLETED: BigInt("547187998251440563"),
                        ERROR: BigInt("114842434023872947"),
                        CANCEL: BigInt("258957622099728819")
                    },
                    response: {
                        NEXT: BigInt("288231212262328384"),
                        COMPLETED: BigInt("432346400338184256"),
                        ERROR: BigInt("836110616640"),
                        CANCEL: BigInt("144116024186472512")
                    }
                },
                annotations: []
            },allocators.responseAllocator(EquityOrderAckResponseTypeDefinition),allocators.requestAllocator(input, OrderIdTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static createOrder(input: PlaceEquityOrderRequest): Observable<EquityOrderResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.equityOrderService.createOrder(input)));
    }

    static updateOrder(input: UpdateEquityOrderRequest): Observable<EquityOrderResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.equityOrderService.updateOrder(input)));
    }

    static getOrders(): Observable<EquityOrderUpdateResult> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.equityOrderService.getOrders()));
    }

    static getOrderById(input: OrderId): Observable<EquityOrderResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.equityOrderService.getOrderById(input)));
    }

    static cancelOrder(input: OrderId): Observable<EquityOrderAckResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.equityOrderService.cancelOrder(input)));
    }
}

export class WatchListService implements IWatchListService {
    getWatchListsByAccountId: () => Observable<GetWatchListsByAccountIdResponse> = () => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WatchListService',
                serviceVersion: 'UW_QR-GzfBQXN5vrTCEyCCGSPJE=',
                majorVersionContentAddress: '0x130b7e8c8b78c3422ebb3582ab8d965f6792122b',
                methodName: 'getWatchListsByAccountId',
                inboundStream: 'empty',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("385058177899696683"),
                        COMPLETED: BigInt("529173365975552555"),
                        ERROR: BigInt("96827801747984939"),
                        CANCEL: BigInt("240942989823840811")
                    },
                    response: {
                        NEXT: BigInt("351281636393168860"),
                        COMPLETED: BigInt("495396824469024732"),
                        ERROR: BigInt("63051260241457116"),
                        CANCEL: BigInt("207166448317312988")
                    }
                },
                annotations: []
            },allocators.responseAllocator(GetWatchListsByAccountIdResponseTypeDefinition));
        };
    getWatchListById: (input: WatchListId) => Observable<WatchListResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WatchListService',
                serviceVersion: 'UW_QR-GzfBQXN5vrTCEyCCGSPJE=',
                majorVersionContentAddress: '0xcf9d5e51a7a8506f6888fd6785cd07ac86f25e03',
                methodName: 'getWatchListById',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("294986516591173123"),
                        COMPLETED: BigInt("439101704667028995"),
                        ERROR: BigInt("6756140439461379"),
                        CANCEL: BigInt("150871328515317251")
                    },
                    response: {
                        NEXT: BigInt("421087459551739771"),
                        COMPLETED: BigInt("565202647627595643"),
                        ERROR: BigInt("132857083400028027"),
                        CANCEL: BigInt("276972271475883899")
                    }
                },
                annotations: []
            },allocators.responseAllocator(WatchListResponseTypeDefinition),allocators.requestAllocator(input, WatchListIdTypeDefinition));
        };
    createWatchList: (input: CreateWatchListRequest) => Observable<WatchListResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WatchListService',
                serviceVersion: 'UW_QR-GzfBQXN5vrTCEyCCGSPJE=',
                majorVersionContentAddress: '0x70ae5c92265cd70297c184b323f8b8dcd90226ca',
                methodName: 'createWatchList',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("310749322822166218"),
                        COMPLETED: BigInt("454864510898022090"),
                        ERROR: BigInt("22518946670454474"),
                        CANCEL: BigInt("166634134746310346")
                    },
                    response: {
                        NEXT: BigInt("405324865015779444"),
                        COMPLETED: BigInt("549440053091635316"),
                        ERROR: BigInt("117094488864067700"),
                        CANCEL: BigInt("261209676939923572")
                    }
                },
                annotations: []
            },allocators.responseAllocator(WatchListResponseTypeDefinition),allocators.requestAllocator(input, CreateWatchListRequestTypeDefinition));
        };
    addAssetToWatchList: (input: AddOrRemoveAssetRequest) => Observable<WatchListResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WatchListService',
                serviceVersion: 'UW_QR-GzfBQXN5vrTCEyCCGSPJE=',
                majorVersionContentAddress: '0xb555ddbe5ab3a70c54665467694fd6132fececae',
                methodName: 'addAssetToWatchList',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("391813249989668014"),
                        COMPLETED: BigInt("535928438065523886"),
                        ERROR: BigInt("103582873837956270"),
                        CANCEL: BigInt("247698061913812142")
                    },
                    response: {
                        NEXT: BigInt("344525805082094681"),
                        COMPLETED: BigInt("488640993157950553"),
                        ERROR: BigInt("56295428930382937"),
                        CANCEL: BigInt("200410617006238809")
                    }
                },
                annotations: []
            },allocators.responseAllocator(WatchListResponseTypeDefinition),allocators.requestAllocator(input, AddOrRemoveAssetRequestTypeDefinition));
        };
    updateWatchList: (input: UpdateWatchListRequest) => Observable<WatchListResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WatchListService',
                serviceVersion: 'UW_QR-GzfBQXN5vrTCEyCCGSPJE=',
                majorVersionContentAddress: '0xca9960dca4c1c6472f32ba780949d44df5d1fc59',
                methodName: 'updateWatchList',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("344525706330504281"),
                        COMPLETED: BigInt("488640894406360153"),
                        ERROR: BigInt("56295330178792537"),
                        CANCEL: BigInt("200410518254648409")
                    },
                    response: {
                        NEXT: BigInt("407575987141789877"),
                        COMPLETED: BigInt("551691175217645749"),
                        ERROR: BigInt("119345610990078133"),
                        CANCEL: BigInt("263460799065934005")
                    }
                },
                annotations: []
            },allocators.responseAllocator(WatchListResponseTypeDefinition),allocators.requestAllocator(input, UpdateWatchListRequestTypeDefinition));
        };
    deleteWatchList: (input: WatchListId) => Observable<WatchListAckResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WatchListService',
                serviceVersion: 'UW_QR-GzfBQXN5vrTCEyCCGSPJE=',
                majorVersionContentAddress: '0x6ded744b281d097660609c398104d9c91331d019',
                methodName: 'deleteWatchList',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("344526235104301081"),
                        COMPLETED: BigInt("488641423180156953"),
                        ERROR: BigInt("56295858952589337"),
                        CANCEL: BigInt("200411047028445209")
                    },
                    response: {
                        NEXT: BigInt("310748722534459594"),
                        COMPLETED: BigInt("454863910610315466"),
                        ERROR: BigInt("22518346382747850"),
                        CANCEL: BigInt("166633534458603722")
                    }
                },
                annotations: []
            },allocators.responseAllocator(WatchListAckResponseTypeDefinition),allocators.requestAllocator(input, WatchListIdTypeDefinition));
        };
    removeAssetFromWatchList: (input: AddOrRemoveAssetRequest) => Observable<WatchListAckResponse> = (input) => {
         return this.hydraPlatform.requestResponse$({
                serviceName: 'WatchListService',
                serviceVersion: 'UW_QR-GzfBQXN5vrTCEyCCGSPJE=',
                majorVersionContentAddress: '0xb6e2d0e11c493934f4f81315ba18188e7012ad7e',
                methodName: 'removeAssetFromWatchList',
                inboundStream: 'one',
                outboundStream: 'one',
                routingData: {
                    request: {
                        NEXT: BigInt("427842576365825406"),
                        COMPLETED: BigInt("571957764441681278"),
                        ERROR: BigInt("139612200214113662"),
                        CANCEL: BigInt("283727388289969534")
                    },
                    response: {
                        NEXT: BigInt("427842770817233662"),
                        COMPLETED: BigInt("571957958893089534"),
                        ERROR: BigInt("139612394665521918"),
                        CANCEL: BigInt("283727582741377790")
                    }
                },
                annotations: []
            },allocators.responseAllocator(WatchListAckResponseTypeDefinition),allocators.requestAllocator(input, AddOrRemoveAssetRequestTypeDefinition));
        };

    constructor(private hydraPlatform: HydraPlatform) {
    }

    static getWatchListsByAccountId(): Observable<GetWatchListsByAccountIdResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.watchListService.getWatchListsByAccountId()));
    }

    static getWatchListById(input: WatchListId): Observable<WatchListResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.watchListService.getWatchListById(input)));
    }

    static createWatchList(input: CreateWatchListRequest): Observable<WatchListResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.watchListService.createWatchList(input)));
    }

    static addAssetToWatchList(input: AddOrRemoveAssetRequest): Observable<WatchListResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.watchListService.addAssetToWatchList(input)));
    }

    static updateWatchList(input: UpdateWatchListRequest): Observable<WatchListResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.watchListService.updateWatchList(input)));
    }

    static deleteWatchList(input: WatchListId): Observable<WatchListAckResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.watchListService.deleteWatchList(input)));
    }

    static removeAssetFromWatchList(input: AddOrRemoveAssetRequest): Observable<WatchListAckResponse> {
        return gatewayServices.pipe(take(1), mergeMap(services => services.watchListService.removeAssetFromWatchList(input)));
    }
}

export interface TradingGatewayServices {
    analyticsService: IAnalyticsService;
    authService: IAuthService;
    blotterService: IBlotterService;
    pricingService: IPricingService;
    referenceDataService: IReferenceDataService;
    throughputAdminService: IThroughputAdminService;
    echoService: IEchoService;
    instrumentService: IInstrumentService;
    dealerService: IDealerService;
    workflowService: IWorkflowService;
    executionService: IExecutionService;
    positionService: IPositionService;
    equityInstrumentService: IEquityInstrumentService;
    accountService: IAccountService;
    marketDataService: IMarketDataService;
    equityOrderService: IEquityOrderService;
    watchListService: IWatchListService;
    versionNegotiationService: VersionNegotiation.IVersionNegotiationService;
    checkCompatibility: () => Observable<VersionNegotiation.Compatibility>;
}

const gatewayServices = new ReplaySubject<TradingGatewayServices>(1);

export function createTradingGatewayServices(hydraPlatform: HydraPlatform): TradingGatewayServices {
    const versionNegotiationService = new VersionNegotiation.VersionNegotiationService(hydraPlatform);
    return {
        analyticsService: new AnalyticsService(hydraPlatform),
        authService: new AuthService(hydraPlatform),
        blotterService: new BlotterService(hydraPlatform),
        pricingService: new PricingService(hydraPlatform),
        referenceDataService: new ReferenceDataService(hydraPlatform),
        throughputAdminService: new ThroughputAdminService(hydraPlatform),
        echoService: new EchoService(hydraPlatform),
        instrumentService: new InstrumentService(hydraPlatform),
        dealerService: new DealerService(hydraPlatform),
        workflowService: new WorkflowService(hydraPlatform),
        executionService: new ExecutionService(hydraPlatform),
        positionService: new PositionService(hydraPlatform),
        equityInstrumentService: new EquityInstrumentService(hydraPlatform),
        accountService: new AccountService(hydraPlatform),
        marketDataService: new MarketDataService(hydraPlatform),
        equityOrderService: new EquityOrderService(hydraPlatform),
        watchListService: new WatchListService(hydraPlatform),
    versionNegotiationService,
        checkCompatibility: () => checkGatewayCompatibility(versionNegotiationService),
    };
}

export function connectToTradingGateway(config: PlatformConfig) {
    const connection = createGatewayConnection(config);
    const hydraPlatform = createHydraPlatform(connection);
    const services = createTradingGatewayServices(hydraPlatform);
    gatewayServices.next(services);
    return {
       connection,
       services,
    };
}

function checkGatewayCompatibility(versionNegotiationService: VersionNegotiation.IVersionNegotiationService): Observable<VersionNegotiation.Compatibility> {
    return versionNegotiationService.checkCompatibility({
                methods: [{
                        serviceName: "AnalyticsService",
                        methodName: "getAnalytics",
                        methodRouteKey: BigInt("398568636411697713"),
                    },{
                        serviceName: "AuthService",
                        methodName: "login",
                        methodRouteKey: BigInt("351281578353370972"),
                    },{
                        serviceName: "AuthService",
                        methodName: "logout",
                        methodRouteKey: BigInt("351281533434447900"),
                    },{
                        serviceName: "BlotterService",
                        methodName: "getTradeStream",
                        methodRouteKey: BigInt("324259965274197136"),
                    },{
                        serviceName: "PricingService",
                        methodName: "getPriceUpdates",
                        methodRouteKey: BigInt("427842701337194174"),
                    },{
                        serviceName: "PricingService",
                        methodName: "getPriceHistory",
                        methodRouteKey: BigInt("351281470395697564"),
                    },{
                        serviceName: "ReferenceDataService",
                        methodName: "getCcyPairs",
                        methodRouteKey: BigInt("301742060701435334"),
                    },{
                        serviceName: "ThroughputAdminService",
                        methodName: "getThroughput",
                        methodRouteKey: BigInt("351281540995897308"),
                    },{
                        serviceName: "ThroughputAdminService",
                        methodName: "setThroughput",
                        methodRouteKey: BigInt("427843003423606206"),
                    },{
                        serviceName: "EchoService",
                        methodName: "echo",
                        methodRouteKey: BigInt("337770967908295446"),
                    },{
                        serviceName: "InstrumentService",
                        methodName: "subscribe",
                        methodRouteKey: BigInt("322008378500144143"),
                    },{
                        serviceName: "DealerService",
                        methodName: "subscribe",
                        methodRouteKey: BigInt("337770542174953878"),
                    },{
                        serviceName: "WorkflowService",
                        methodName: "createRfq",
                        methodRouteKey: BigInt("317504155252739341"),
                    },{
                        serviceName: "WorkflowService",
                        methodName: "cancelRfq",
                        methodRouteKey: BigInt("376051112494117735"),
                    },{
                        serviceName: "WorkflowService",
                        methodName: "quote",
                        methodRouteKey: BigInt("403072499992824307"),
                    },{
                        serviceName: "WorkflowService",
                        methodName: "pass",
                        methodRouteKey: BigInt("353533475228521565"),
                    },{
                        serviceName: "WorkflowService",
                        methodName: "accept",
                        methodRouteKey: BigInt("400821014667493746"),
                    },{
                        serviceName: "WorkflowService",
                        methodName: "subscribe",
                        methodRouteKey: BigInt("360288726900984608"),
                    },{
                        serviceName: "ExecutionService",
                        methodName: "executeTrade",
                        methodRouteKey: BigInt("340022560985478551"),
                    },{
                        serviceName: "PositionService",
                        methodName: "getPositions",
                        methodRouteKey: BigInt("313000950613713291"),
                    },{
                        serviceName: "EquityInstrumentService",
                        methodName: "getInstruments",
                        methodRouteKey: BigInt("403073159606372467"),
                    },{
                        serviceName: "AccountService",
                        methodName: "getAccountSummary",
                        methodRouteKey: BigInt("385058098931970859"),
                    },{
                        serviceName: "MarketDataService",
                        methodName: "getHistoricalBars",
                        methodRouteKey: BigInt("288231296120513856"),
                    },{
                        serviceName: "MarketDataService",
                        methodName: "getLatestQuote",
                        methodRouteKey: BigInt("328762871565519186"),
                    },{
                        serviceName: "MarketDataService",
                        methodName: "getLatestBars",
                        methodRouteKey: BigInt("407575775326712309"),
                    },{
                        serviceName: "MarketDataService",
                        methodName: "subscribeToMarketStatus",
                        methodRouteKey: BigInt("423339073311874044"),
                    },{
                        serviceName: "EquityOrderService",
                        methodName: "createOrder",
                        methodRouteKey: BigInt("297237667075461124"),
                    },{
                        serviceName: "EquityOrderService",
                        methodName: "updateOrder",
                        methodRouteKey: BigInt("403073030891698483"),
                    },{
                        serviceName: "EquityOrderService",
                        methodName: "getOrders",
                        methodRouteKey: BigInt("355785456691541918"),
                    },{
                        serviceName: "EquityOrderService",
                        methodName: "getOrderById",
                        methodRouteKey: BigInt("326511280314556113"),
                    },{
                        serviceName: "EquityOrderService",
                        methodName: "cancelOrder",
                        methodRouteKey: BigInt("403072810175584691"),
                    },{
                        serviceName: "WatchListService",
                        methodName: "getWatchListsByAccountId",
                        methodRouteKey: BigInt("385058177899696683"),
                    },{
                        serviceName: "WatchListService",
                        methodName: "getWatchListById",
                        methodRouteKey: BigInt("294986516591173123"),
                    },{
                        serviceName: "WatchListService",
                        methodName: "createWatchList",
                        methodRouteKey: BigInt("310749322822166218"),
                    },{
                        serviceName: "WatchListService",
                        methodName: "addAssetToWatchList",
                        methodRouteKey: BigInt("391813249989668014"),
                    },{
                        serviceName: "WatchListService",
                        methodName: "updateWatchList",
                        methodRouteKey: BigInt("344525706330504281"),
                    },{
                        serviceName: "WatchListService",
                        methodName: "deleteWatchList",
                        methodRouteKey: BigInt("344526235104301081"),
                    },{
                        serviceName: "WatchListService",
                        methodName: "removeAssetFromWatchList",
                        methodRouteKey: BigInt("427842576365825406"),
                    },
                ],
                checkHandlersAreRegistered: true,
                methodRouteKeyFormatVersion: 1,
                clientSupportsOlderMessageFormats: false,
            });
}

export function checkCompatibility(): Observable<VersionNegotiation.Compatibility> {
    return gatewayServices.pipe(
                        take(1),
                        mergeMap(services => checkGatewayCompatibility(services.versionNegotiationService))
                    );
}
